import React, { useCallback, useEffect, useMemo, useState } from "react"
import { User, Users, Wallet, PlusCircle, Info, ChevronDown, ChevronUp, Trophy, Layers } from "lucide-react"
import api from "../../utils/api"
import { formatDateLong, formatDateShort, formatMoney } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"
import { getApiErrorMessage } from "../../utils/errorMessage"

const toDateInputValue = (value) => {
  if (!value) return ""
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ""
  return dt.toISOString().slice(0, 10)
}

const getDateRangeBounds = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null
  const end = endDate ? new Date(endDate) : null
  if (start) start.setHours(0, 0, 0, 0)
  if (end) end.setHours(23, 59, 59, 999)
  return { start, end }
}

const filterRowsByDateRange = (
  rows = [],
  startDate = "",
  endDate = "",
  getRowDate = (row) => row?.date
) => {
  const { start, end } = getDateRangeBounds(startDate, endDate)
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const d = new Date(getRowDate(row))
    if (Number.isNaN(d.getTime())) return false
    if (start && d < start) return false
    if (end && d > end) return false
    return true
  })
}

const getLastTwoMonthsDateRange = () => {
  const end = new Date()
  const start = new Date(end)
  start.setMonth(start.getMonth() - 2)
  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  }
}

const resolveSecondRoundStartAt = (cycle) => {
  if (!cycle) return null
  const rounds = Array.isArray(cycle?.rounds) ? cycle.rounds : []
  const roundTwo = rounds.find((round) => Number(round?.roundNumber || 0) === 2)
  const roundTwoStartAt = roundTwo?.invoiceScheduledAt ? new Date(roundTwo.invoiceScheduledAt) : null
  if (roundTwoStartAt && !Number.isNaN(roundTwoStartAt.getTime())) return roundTwoStartAt
  const startDate = cycle?.startDate ? new Date(cycle.startDate) : null
  if (!startDate || Number.isNaN(startDate.getTime())) return null
  const intervalDays = Number(cycle?.intervalDays || 0)
  if (!(intervalDays > 0)) return startDate
  const secondRoundStartAt = new Date(startDate)
  secondRoundStartAt.setDate(secondRoundStartAt.getDate() + intervalDays)
  return secondRoundStartAt
}

const isFirstRoundEnrollmentWindowOpen = (cycle) => {
  if (!cycle) return true
  if (!["scheduled", "active"].includes(String(cycle?.status || "").toLowerCase())) return false
  const secondRoundStartAt = resolveSecondRoundStartAt(cycle)
  if (!secondRoundStartAt || Number.isNaN(secondRoundStartAt.getTime())) return false
  return Date.now() < secondRoundStartAt.getTime()
}

const isInsufficientGroupsWalletMessage = (message) => {
  const normalized = String(message || "").toLowerCase()
  return normalized.includes("insufficient") && normalized.includes("wallet")
}

const buildInsufficientFundsPopupMessage = ({ requiredAmount, currentBalance }) => {
  const required = Number(requiredAmount || 0)
  const current = Number(currentBalance || 0)
  const shortfall = Math.max(0, required - current)
  return [
    "Insufficient funds in your Groups Wallet.",
    `Required: ${formatMoney(required)}`,
    `Current balance: ${formatMoney(current)}`,
    `Shortfall: ${formatMoney(shortfall)}`,
  ].join("\n")
}

const hasStoredMoneyValue = (value) =>
  value !== null && value !== undefined && Number.isFinite(Number(value))

const applyWalletRowTotals = (rows = [], currentBalance = 0) => {
  let runningTotal = Number(currentBalance || 0)
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const hasBalanceAfter = hasStoredMoneyValue(row?.balanceAfter)
    const hasBalanceBefore = hasStoredMoneyValue(row?.balanceBefore)
    const total = hasBalanceAfter ? Number(row.balanceAfter) : runningTotal
    runningTotal = hasBalanceBefore
      ? Number(row.balanceBefore)
      : Number((total - Number(row?.amount || 0)).toFixed(2))
    return { ...row, total }
  })
}

const stripChamaaCycleMarkers = (description) => {
  const text = String(description || "").trim()
  if (!text) return ""
  return text
    .replace(/\s*\(C\d+\s*\/\s*R\d+(?:\s*,?\s*S\d+)?\)\s*$/gi, "")
    .replace(/\s*C\d+\s*\/\s*R\d+(?:\s*,?\s*S\d+)?\s*$/gi, "")
    .trim()
}

const formatWelfareMemberIdentifier = (identifier, fallbackMemberNumber = 0) => {
  const raw = String(identifier || "").trim()
  if (raw) return raw.replace(/^#/i, "M")
  const number = Number(fallbackMemberNumber || 0)
  return number > 0 ? `M${number}` : ""
}

const invertInOutPrefix = (description) => {
  const text = String(description || "").trim()
  if (!text) return ""
  if (/^IN\s*-\s*/i.test(text)) {
    return text.replace(/^IN(\s*-\s*)/i, "OUT$1")
  }
  if (/^OUT\s*-\s*/i.test(text)) {
    return text.replace(/^OUT(\s*-\s*)/i, "IN$1")
  }
  return text
}

const toTitleCase = (value) => {
  const text = String(value || "").trim()
  if (!text) return ""
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

const extractRelationFromText = (text) => {
  const source = String(text || "")
  const match = source.match(/\(([^)]+)\)/)
  if (!match) return ""
  return toTitleCase(match[1] || "")
}

const getWelfareMemberStatusPillClass = (statusKey) => {
  const key = String(statusKey || "").toLowerCase()
  if (key === "good_standing") return "bg-emerald-50 border-emerald-200 text-emerald-700"
  if (key === "late_overdue") return "bg-amber-50 border-amber-200 text-amber-700"
  if (key === "suspended_abandoned" || key === "suspended_default") {
    return "bg-red-50 border-red-200 text-red-700"
  }
  return "bg-gray-50 border-gray-200 text-gray-700"
}

const getCycleMemberStatusMeta = (member) => {
  const due = Number(member?.dueAmount || 0)
  if (member?.alreadyWon) {
    return {
      label: member?.wonRound ? `Recipient · R${member.wonRound}` : "Recipient",
      className: "bg-amber-100 text-amber-700 border-amber-200",
    }
  }
  if (due > 0) {
    return {
      label: "Has Balance",
      className: "bg-red-100 text-red-700 border-red-200",
    }
  }
  return {
    label: "Up to Date",
    className: "bg-emerald-100 text-emerald-700 border-emerald-200",
  }
}

const resolveInvoiceDisplayStatus = (invoice, nowMs = Date.now()) => {
  const rawStatus = String(invoice?.status || "").toLowerCase()
  if (!["pending", "partial"].includes(rawStatus)) return rawStatus
  const dueDateMs = new Date(invoice?.dueDate || 0).getTime()
  if (!Number.isFinite(dueDateMs)) return rawStatus
  return dueDateMs < nowMs ? "overdue" : rawStatus
}

const normalizeWelfareProbationDuration = (probation) => {
  const value = Number(probation?.value || 0)
  const unit = String(probation?.unit || "").trim().toLowerCase()
  if (!Number.isInteger(value) || value <= 0) return null
  if (!["days", "months"].includes(unit)) return null
  return { value, unit }
}

const shiftDateByProbationDuration = (date, probation, direction = 1) => {
  if (!date || Number.isNaN(date.getTime()) || !probation) return null
  const next = new Date(date)
  const delta = Number(direction || 1) * Number(probation.value || 0)
  if (probation.unit === "days") {
    next.setDate(next.getDate() + delta)
  } else {
    next.setMonth(next.getMonth() + delta)
  }
  return next
}

const parseSafeDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const resolveWelfareProbationDisplay = ({ group, memberInfo }) => {
  const now = new Date()
  const probationDuration = normalizeWelfareProbationDuration(group?.welfare?.probation || null)
  const probationReason = String(memberInfo?.welfareProbationReason || "").trim().toLowerCase()

  const latePaymentEnd = parseSafeDate(memberInfo?.welfareProbationEndsAt)
  const latePaymentStartRaw = parseSafeDate(memberInfo?.welfareProbationStartedAt)
  const latePaymentStart =
    latePaymentStartRaw ||
    (latePaymentEnd && probationDuration
      ? shiftDateByProbationDuration(latePaymentEnd, probationDuration, -1)
      : null)

  const registrationEligibleAt = parseSafeDate(memberInfo?.registrationEligibleAt)
  const registrationInvoicedAt = parseSafeDate(memberInfo?.registrationInvoicedAt)
  const isEnrollmentProbation =
    !!registrationEligibleAt &&
    registrationEligibleAt.getTime() > now.getTime() &&
    !registrationInvoicedAt

  if (isEnrollmentProbation) {
    const enrollmentEnd = parseSafeDate(group?.welfare?.enrollment?.endDate)
    const enrollmentStart =
      enrollmentEnd ||
      (registrationEligibleAt && probationDuration
        ? shiftDateByProbationDuration(registrationEligibleAt, probationDuration, -1)
        : null)

    return {
      isOnProbation: true,
      scenarioLabel: "Enrollment probation",
      startAt: enrollmentStart,
      endAt: registrationEligibleAt,
    }
  }

  const isProbation =
    String(memberInfo?.welfareStatus || "").toLowerCase() === "probation" &&
    (!latePaymentEnd || latePaymentEnd.getTime() > now.getTime())

  if (!isProbation) return null

  const scenarioLabel =
    probationReason === "late_payment"
      ? "Late-payment probation"
      : probationReason === "join"
        ? "Joining probation"
        : "Probation"

  return {
    isOnProbation: true,
    scenarioLabel,
    startAt: latePaymentStart,
    endAt:
      latePaymentEnd ||
      (latePaymentStart && probationDuration
        ? shiftDateByProbationDuration(latePaymentStart, probationDuration, 1)
        : null),
  }
}

function GroupMemberFinancePanel({ group, onGroupUpdated, onUpdateMaxShares, readOnly = false }) {
  const theme = getGroupTheme(group)
  const [walletBalance, setWalletBalance] = useState(0)
  const [ledgerGroupWalletBalance, setLedgerGroupWalletBalance] = useState(null)
  const [memberInfo, setMemberInfo] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [allMemberPayments, setAllMemberPayments] = useState([])
  const [groupWalletTransactions, setGroupWalletTransactions] = useState([])
  const [allGroupInvoices, setAllGroupInvoices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isPayingId, setIsPayingId] = useState("")
  const [desiredSharesInput, setDesiredSharesInput] = useState("1")
  const [maxSharesInput, setMaxSharesInput] = useState("1")
  const [isSavingDesiredShares, setIsSavingDesiredShares] = useState(false)
  const [isSavingMaxShares, setIsSavingMaxShares] = useState(false)
  const [cycleMembersSearch, setCycleMembersSearch] = useState("")
  const [cycleMembersVisibleCount, setCycleMembersVisibleCount] = useState(5)
  const [projectMembersVisibleCount, setProjectMembersVisibleCount] = useState(5)
  const [invoiceScope, setInvoiceScope] = useState("all")
  const [outstandingVisibleCount, setOutstandingVisibleCount] = useState(5)
  const [isWalletTransactionsOpen, setIsWalletTransactionsOpen] = useState(false)
  const [isMyOutstandingOpen, setIsMyOutstandingOpen] = useState(false)
  const [isEnrollmentSectionOpen, setIsEnrollmentSectionOpen] = useState(false)
  const [walletTransactionsSearch, setWalletTransactionsSearch] = useState("")
  const [walletTransactionsCycleFilter, setWalletTransactionsCycleFilter] = useState("all")
  const [walletTransactionsFilterOpen, setWalletTransactionsFilterOpen] = useState(false)
  const [walletTransactionsStartDate, setWalletTransactionsStartDate] = useState("")
  const [walletTransactionsEndDate, setWalletTransactionsEndDate] = useState("")
  const [walletTransactionsAppliedStartDate, setWalletTransactionsAppliedStartDate] = useState("")
  const [walletTransactionsAppliedEndDate, setWalletTransactionsAppliedEndDate] = useState("")
  const [isAddBillModalOpen, setIsAddBillModalOpen] = useState(false)
  const [billAmountInput, setBillAmountInput] = useState("")
  const [billDescriptionInput, setBillDescriptionInput] = useState("")
  const [isAddingBill, setIsAddingBill] = useState(false)
  const [pendingProxyPaymentInvoice, setPendingProxyPaymentInvoice] = useState(null)
  const [pendingCycleMemberPayment, setPendingCycleMemberPayment] = useState(null)
  const [proxyInvoicePaymentConfirmed, setProxyInvoicePaymentConfirmed] = useState(false)
  const [proxyCyclePaymentConfirmed, setProxyCyclePaymentConfirmed] = useState(false)
  const [cycleMemberPaymentMode, setCycleMemberPaymentMode] = useState("full")
  const [cycleMemberPaymentAmount, setCycleMemberPaymentAmount] = useState("")
  const [paymentAmountInput, setPaymentAmountInput] = useState("")
  const [projectPerPersonInput, setProjectPerPersonInput] = useState("")
  const [projectTotalTargetInput, setProjectTotalTargetInput] = useState("")
  const [projectClosingDateInput, setProjectClosingDateInput] = useState("")
  const [projectTargetDraftByMember, setProjectTargetDraftByMember] = useState({})
  const [isSavingProjectSettings, setIsSavingProjectSettings] = useState(false)
  const [isSavingProjectTargets, setIsSavingProjectTargets] = useState(false)
  const [isRemovingProjectMemberId, setIsRemovingProjectMemberId] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const isChamaaGroup = !!group?.isChamaa
  const isProjectSavingsGroup = !!group?.isProjectSavings
  const isWelfareGroup = !!group?.isWelfare
  const isAdmin = String(group?.myRole || "").toLowerCase() === "admin"
  const currentMemberId = String(memberInfo?.id || group?.myMemberId || "")
  const activeCycle = group?.activeCycle || null
  const activeCycleId = String(activeCycle?.id || activeCycle?._id || "")
  const activeCycleSequence = Number(activeCycle?.sequenceNumber || 0) || null
  const projectSavingsMemberCount = Array.isArray(group?.members) ? group.members.length : 0
  const welfareStatusInfo = memberInfo?.welfareMemberStatus || null
  const welfareStatusLabel = welfareStatusInfo?.label || "Good Standing"
  const welfareStatusPillClass = getWelfareMemberStatusPillClass(welfareStatusInfo?.key)
  const welfareProbationDisplay = resolveWelfareProbationDisplay({ group, memberInfo })
  const myTransactionsLabel = isWelfareGroup ? "My Welfare Transactions" : "My Outstanding"
  const myWalletBalanceLabel = isChamaaGroup ? "My Wallet Balance" : "My Groups Wallet"
  const normalizeTransactionDescription = useCallback(
    (value) => (isChamaaGroup ? String(value || "").trim() : stripChamaaCycleMarkers(value)),
    [isChamaaGroup]
  )

  const loadData = async () => {
    if (!group?.id) return
    setIsLoading(true)
    setError("")
    try {
      const [txRes, walletRes, ledgerRes] = await Promise.all([
        api.get(`/groups/${group.id}/transactions/my`),
        api.get("/wallet/groups/my-wallet"),
        api.get(`/groups/${group.id}/admin/ledger`),
      ])

      setMemberInfo(txRes?.data?.member || null)
      setInvoices(Array.isArray(txRes?.data?.invoices) ? txRes.data.invoices : [])
      setAllMemberPayments(Array.isArray(ledgerRes?.data?.payments) ? ledgerRes.data.payments : [])
      setGroupWalletTransactions(
        Array.isArray(ledgerRes?.data?.walletTransactions) ? ledgerRes.data.walletTransactions : []
      )
      setLedgerGroupWalletBalance(
        Number(ledgerRes?.data?.summary?.groupWalletBalance ?? group?.groupWalletBalance ?? 0)
      )
      setAllGroupInvoices(Array.isArray(ledgerRes?.data?.invoices) ? ledgerRes.data.invoices : [])
      setWalletBalance(Number(walletRes?.data?.wallet?.balance ?? 0))
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to load your group ledger.")
      setError(message)
      setAllMemberPayments([])
      setGroupWalletTransactions([])
      setAllGroupInvoices([])
      setLedgerGroupWalletBalance(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, group?.groupWalletBalance, group?.activeCycle?.id])

  useEffect(() => {
    const memberId = String(memberInfo?.id || "")
    if (!memberId || !Array.isArray(group?.members)) return
    const ownEntry = group.members.find((member) => String(member.id) === memberId)
    if (!ownEntry) return
    setDesiredSharesInput(String(ownEntry.desiredShares || 1))
    setMaxSharesInput(String(group?.maxSharesPerMember || 1))
  }, [group?.members, memberInfo?.id, group?.maxSharesPerMember])

  useEffect(() => {
    if (!isProjectSavingsGroup) {
      setProjectPerPersonInput("")
      setProjectTotalTargetInput("")
      setProjectClosingDateInput("")
      setProjectTargetDraftByMember({})
      return
    }

    const perPersonAmount = Number(group?.amountPerPerson ?? group?.projectSavingsGoalAmount ?? 0)
    const totalTargetAmount = perPersonAmount * projectSavingsMemberCount
    setProjectPerPersonInput(perPersonAmount > 0 ? String(perPersonAmount) : "")
    setProjectTotalTargetInput(totalTargetAmount > 0 ? String(totalTargetAmount) : "")
    setProjectClosingDateInput(
      toDateInputValue(group?.projectSavingsClosingDate || group?.eventDate || null)
    )

    const targetDraft = {}
    for (const member of group?.members || []) {
      targetDraft[String(member?.id || "")] = String(
        Number(member?.projectSavingsTargetAmount ?? member?.expected ?? 0) || 0
      )
    }
    setProjectTargetDraftByMember(targetDraft)
  }, [
    group?.id,
    group?.members,
    group?.amountPerPerson,
    group?.projectSavingsGoalAmount,
    group?.projectSavingsClosingDate,
    group?.totalBudget,
    group?.eventDate,
    projectSavingsMemberCount,
    isProjectSavingsGroup,
  ])

  const handleProjectPerPersonInputChange = (value) => {
    setProjectPerPersonInput(value)
    const perPersonAmount = Number(value)
    if (!value.trim() || !Number.isFinite(perPersonAmount) || perPersonAmount < 0) {
      setProjectTotalTargetInput("")
      return
    }
    setProjectTotalTargetInput(String(Number((perPersonAmount * projectSavingsMemberCount).toFixed(2))))
  }

  const handleProjectTotalTargetInputChange = (value) => {
    setProjectTotalTargetInput(value)
    const totalTargetAmount = Number(value)
    if (!value.trim() || !Number.isFinite(totalTargetAmount) || totalTargetAmount < 0) {
      setProjectPerPersonInput("")
      return
    }
    if (projectSavingsMemberCount <= 0) {
      setProjectPerPersonInput(String(Number(totalTargetAmount.toFixed(2))))
      return
    }
    setProjectPerPersonInput(
      String(Number((totalTargetAmount / projectSavingsMemberCount).toFixed(2)))
    )
  }

  const handlePayInvoice = async (invoice, requestedAmount = null) => {
    if (!group?.id || !invoice?.id) return
    setError("")
    setSuccess("")

    const currentBalance = Number(invoice.balanceAmount || 0)
    if (!(currentBalance > 0)) {
      setError("Invoice balance must be greater than 0.")
      return false
    }

    const amount = requestedAmount === null ? currentBalance : Number(requestedAmount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Payment amount must be greater than 0.")
      return false
    }
    if (amount > currentBalance) {
      setError("Payment amount cannot be greater than the outstanding balance.")
      return false
    }
    if (Number(walletBalance || 0) < amount) {
      const insufficientMessage = buildInsufficientFundsPopupMessage({
        requiredAmount: amount,
        currentBalance: walletBalance,
      })
      window.alert(insufficientMessage)
      setError("Insufficient funds in your Groups Wallet.")
      return false
    }

    setIsPayingId(String(invoice.id))
    try {
      const res = await api.post(`/groups/${group.id}/invoices/${invoice.id}/pay`, { amount })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setSuccess("Invoice paid successfully.")
      await loadData()
      return true
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to pay invoice.")
      if (isInsufficientGroupsWalletMessage(message)) {
        window.alert("Insufficient funds in your Groups Wallet. Please add funds and try again.")
      }
      setError(message)
      return false
    } finally {
      setIsPayingId("")
    }
  }

  const handleRequestPayInvoice = async (invoice) => {
    const invoiceMemberId = String(invoice?.memberId || memberInfo?.id || "")
    const oldestOutstandingInvoice = oldestOutstandingInvoiceByMember.get(invoiceMemberId)
    if (
      isChamaaGroup &&
      oldestOutstandingInvoice &&
      String(oldestOutstandingInvoice?.id || "") !== String(invoice?.id || "")
    ) {
      setError(
        `You must first pay for Round ${oldestOutstandingInvoice?.roundNumber || "-"}.`
      )
      return
    }
    setPendingProxyPaymentInvoice(invoice)
    setPaymentAmountInput(String(Number(invoice?.balanceAmount || 0) || ""))
    setProxyInvoicePaymentConfirmed(false)
  }

  const handlePayCycleMemberBalance = async (member, amount) => {
    if (!group?.id || !member?.id) return false
    if (isChamaaGroup && !activeCycleId) return false
    setError("")
    setSuccess("")

    const paymentAmount = Number(amount)
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      setError("Payment amount must be greater than 0.")
      return false
    }
    if (Number(walletBalance || 0) < paymentAmount) {
      const insufficientMessage = buildInsufficientFundsPopupMessage({
        requiredAmount: paymentAmount,
        currentBalance: walletBalance,
      })
      window.alert(insufficientMessage)
      setError("Insufficient funds in your Groups Wallet.")
      return false
    }

    const payingKey = `member:${member.id}`
    setIsPayingId(payingKey)
    try {
      let res = null
      if (isChamaaGroup) {
        res = await api.post(`/groups/${group.id}/cycles/${activeCycleId}/members/${member.id}/pay`, {
          amount: paymentAmount,
        })
      } else if (isWelfareGroup) {
        if (String(member?.id || "") !== String(currentMemberId || "")) {
          setError("Welfare balance payment is only available for your own outstanding invoices.")
          return false
        }
        res = await api.post(`/groups/${group.id}/invoices/my/pay`, {
          amount: paymentAmount,
        })
      } else {
        return false
      }
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      if (isWelfareGroup) {
        const appliedInvoiceCount = Number(res?.data?.appliedInvoiceCount || 0)
        setSuccess(
          appliedInvoiceCount > 0
            ? `Paid ${appliedInvoiceCount} oldest invoice${appliedInvoiceCount === 1 ? "" : "s"} successfully.`
            : "Outstanding balance paid successfully."
        )
      } else {
        setSuccess("Member balance paid successfully.")
      }
      await loadData()
      return true
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        isWelfareGroup ? "Failed to pay outstanding balance." : "Failed to pay member balance."
      )
      if (isInsufficientGroupsWalletMessage(message)) {
        window.alert("Insufficient funds in your Groups Wallet. Please add funds and try again.")
      }
      setError(message)
      return false
    } finally {
      setIsPayingId("")
    }
  }

  const handleRequestMyWelfareBalancePayment = () => {
    if (!isWelfareGroup || !currentMemberId) return
    const outstandingInvoices = myWelfareOutstandingInvoicesByOldest
    const totalDue = Number(
      outstandingInvoices.reduce((sum, invoice) => sum + Number(invoice?.balanceAmount || 0), 0)
    )
    if (!(totalDue > 0)) {
      setError("You do not have outstanding invoices to pay.")
      return
    }
    const oldestInvoiceAmount = Number(outstandingInvoices?.[0]?.balanceAmount || 0)
    setPendingCycleMemberPayment({
      memberId: String(currentMemberId),
      memberName: memberInfo?.name || "You",
      totalDue,
      shareUnitAmount: oldestInvoiceAmount,
      invoiceBalances: outstandingInvoices.map((invoice) => Number(invoice?.balanceAmount || 0)),
      isMine: true,
    })
    setCycleMemberPaymentMode("full")
    setCycleMemberPaymentAmount(String(totalDue))
    setProxyCyclePaymentConfirmed(false)
  }

  const handleRequestCycleMemberPayment = (member) => {
    if (!member?.id || !(Number(member?.dueAmount || 0) > 0)) return
    const totalDue = Number(member.dueAmount || 0)
    const outstandingInvoices = currentCycleOutstandingInvoicesByMember.get(String(member.id)) || []
    const oldestInvoiceAmount = Number(outstandingInvoices?.[0]?.balanceAmount || 0)
    setPendingCycleMemberPayment({
      memberId: String(member.id),
      memberName: member.name || "Member",
      totalDue,
      shareUnitAmount: oldestInvoiceAmount,
      invoiceBalances: outstandingInvoices.map((invoice) => Number(invoice?.balanceAmount || 0)),
      isMine: String(member.id) === String(memberInfo?.id || ""),
    })
    setCycleMemberPaymentMode("full")
    setCycleMemberPaymentAmount(String(totalDue))
    setProxyCyclePaymentConfirmed(false)
  }

  const closePendingPaymentModal = () => {
    if (String(isPayingId || "") === String(pendingProxyPaymentInvoice?.id || "")) return
    setPendingProxyPaymentInvoice(null)
    setPaymentAmountInput("")
    setProxyInvoicePaymentConfirmed(false)
  }

  const closePendingCycleMemberPaymentModal = () => {
    if (String(isPayingId || "") === `member:${pendingCycleMemberPayment?.memberId || ""}`) return
    setPendingCycleMemberPayment(null)
    setCycleMemberPaymentMode("full")
    setCycleMemberPaymentAmount("")
    setProxyCyclePaymentConfirmed(false)
  }

  const handleSelectedPayment = async () => {
    if (!pendingProxyPaymentInvoice) return
    const didPay = await handlePayInvoice(pendingProxyPaymentInvoice, paymentAmountInput)
    if (didPay) {
      setPendingProxyPaymentInvoice(null)
      setPaymentAmountInput("")
    }
  }

  const handleSelectedCycleMemberPayment = async () => {
    if (!pendingCycleMemberPayment) return
    const didPay = await handlePayCycleMemberBalance(
      { id: pendingCycleMemberPayment.memberId },
      cycleMemberPaymentAmount
    )
    if (didPay) {
      setPendingCycleMemberPayment(null)
      setCycleMemberPaymentMode("full")
      setCycleMemberPaymentAmount("")
    }
  }

  const canPayInvoices = !!memberInfo?.id
  const billAmountValidationMessage = useMemo(() => {
    if (!isAddBillModalOpen) return ""
    const raw = String(billAmountInput || "").trim()
    if (!raw) return "Enter bill amount."
    const amount = Number(raw)
    if (!Number.isFinite(amount)) return "Enter a valid bill amount."
    if (amount <= 0) return "Bill amount must be greater than 0."
    const currentGroupWalletBalance = Number(ledgerGroupWalletBalance ?? group?.groupWalletBalance ?? 0)
    if (amount > currentGroupWalletBalance) {
      return "Bill amount cannot be greater than current group wallet balance."
    }
    return ""
  }, [billAmountInput, isAddBillModalOpen, ledgerGroupWalletBalance, group?.groupWalletBalance])
  const billDescriptionValidationMessage = useMemo(() => {
    if (!isAddBillModalOpen) return ""
    const description = String(billDescriptionInput || "").trim()
    if (!description) return "Enter description."
    return ""
  }, [billDescriptionInput, isAddBillModalOpen])
  const addBillValidationMessage =
    billDescriptionValidationMessage || billAmountValidationMessage
  const pendingPaymentValidationMessage = useMemo(() => {
    if (!pendingProxyPaymentInvoice) return ""
    const raw = String(paymentAmountInput || "").trim()
    if (!raw) return ""

    const amount = Number(raw)
    const maxAmount = Number(pendingProxyPaymentInvoice?.balanceAmount || 0)
    if (!Number.isFinite(amount)) return "Enter a valid payment amount."
    if (amount < 0) return "Payment amount cannot be negative."
    if (amount === 0) return "Payment amount must be greater than 0."
    if (amount > maxAmount) {
      return `Payment amount cannot be greater than ${formatMoney(maxAmount)}.`
    }
    if (isChamaaGroup && Math.round(amount * 100) !== Math.round(maxAmount * 100)) {
      return "Chamaa invoice payments must settle the full oldest unpaid invoice."
    }
    return ""
  }, [isChamaaGroup, pendingProxyPaymentInvoice, paymentAmountInput])

  const cycleMemberPaymentOptions = useMemo(() => {
    if (!pendingCycleMemberPayment) return []
    const options = []
    let cumulativeAmount = 0

    for (const balance of pendingCycleMemberPayment?.invoiceBalances || []) {
      const invoiceBalance = Number(balance || 0)
      if (!(invoiceBalance > 0)) continue
      cumulativeAmount = Number((cumulativeAmount + invoiceBalance).toFixed(2))
      options.push(cumulativeAmount)
    }

    return options
  }, [pendingCycleMemberPayment])

  const cycleMemberPaymentValidationMessage = useMemo(() => {
    if (!pendingCycleMemberPayment) return ""
    const amount = Number(cycleMemberPaymentAmount)
    const totalDue = Number(pendingCycleMemberPayment?.totalDue || 0)
    if (!Number.isFinite(amount) || amount <= 0) return "Choose a valid payment amount."
    if (amount > totalDue) return "Payment amount cannot be greater than the outstanding balance."
    if (cycleMemberPaymentMode === "full") {
      return amount === totalDue ? "" : "Full balance payment must match the total outstanding amount."
    }
    const amountCents = Math.round(amount * 100)
    const validPaymentAmountCents = new Set(
      cycleMemberPaymentOptions.map((option) => Math.round(Number(option || 0) * 100))
    )
    return validPaymentAmountCents.has(amountCents)
      ? ""
      : "Payment amount must fully cover the oldest invoice(s) in order."
  }, [cycleMemberPaymentAmount, cycleMemberPaymentMode, cycleMemberPaymentOptions, pendingCycleMemberPayment])

  const selectedCycleMemberPaidInvoiceCount = useMemo(() => {
    if (!pendingCycleMemberPayment) return 0
    let remainingAmount = Number(cycleMemberPaymentAmount || 0)
    if (!Number.isFinite(remainingAmount) || remainingAmount <= 0) return 0

    let paidInvoiceCount = 0
    for (const balance of pendingCycleMemberPayment?.invoiceBalances || []) {
      const invoiceBalance = Number(balance || 0)
      if (!(invoiceBalance > 0)) continue
      if (remainingAmount + 0.0001 < invoiceBalance) break
      remainingAmount = Number((remainingAmount - invoiceBalance).toFixed(2))
      paidInvoiceCount += 1
      if (remainingAmount <= 0) break
    }

    return paidInvoiceCount
  }, [cycleMemberPaymentAmount, pendingCycleMemberPayment])

  const isEnrollmentOpen = useMemo(() => {
    if (!isChamaaGroup) return false
    return isFirstRoundEnrollmentWindowOpen(activeCycle)
  }, [activeCycle, isChamaaGroup])

  const ownMemberEntry = useMemo(() => {
    const memberId = String(memberInfo?.id || "")
    if (!memberId || !Array.isArray(group?.members)) return null
    return group.members.find((member) => String(member.id) === memberId) || null
  }, [group?.members, memberInfo?.id])

  const currentDesiredShares = Number(ownMemberEntry?.desiredShares || 1)
  const maxAllowedShares = Number(group?.maxSharesPerMember || 1)
  const enrollmentSharesBlockedMessage = useMemo(() => {
    if (!isChamaaGroup) return ""
    if (isEnrollmentOpen) return ""
    if (currentDesiredShares >= maxAllowedShares) {
      return `You already have the maximum ${maxAllowedShares} share${maxAllowedShares === 1 ? "" : "s"} for this cycle.`
    }
    return "You cannot increase shares after round 1 enrollment closes. Use the Next Cycle tab for future-cycle changes."
  }, [currentDesiredShares, isChamaaGroup, isEnrollmentOpen, maxAllowedShares])

  const handleUpdateDesiredShares = async () => {
    if (!group?.id) return
    setError("")
    setSuccess("")
    const shares = Number(desiredSharesInput || 1)
    if (!Number.isFinite(shares) || shares < 1) {
      setError("Shares must be at least 1.")
      return
    }
    if (!Number.isInteger(shares)) {
      setError("Shares must be a whole number.")
      return
    }
    if (!isEnrollmentOpen && shares > currentDesiredShares) {
      setError(
        enrollmentSharesBlockedMessage ||
        "You cannot increase shares after round 1 enrollment closes."
      )
      return
    }
    if (Number.isFinite(maxAllowedShares) && shares > maxAllowedShares) {
      setError(`Share limit for this cycle is ${maxAllowedShares}. You cannot increase above that.`)
      return
    }
    if (!isEnrollmentOpen && shares === currentDesiredShares) {
      setError("Round 1 enrollment is closed, so share increases are locked for this cycle.")
      return
    }

    setIsSavingDesiredShares(true)
    try {
      const res = await api.patch(`/groups/${group.id}/enrollment/my-shares`, {
        desiredShares: shares,
      })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setSuccess(res?.data?.message || "Desired shares updated.")
      await loadData()
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to update shares.")
      setError(message)
    } finally {
      setIsSavingDesiredShares(false)
    }
  }

  const handleUpdateMaxShares = async () => {
    if (!group?.id || !onUpdateMaxShares) return
    setError("")
    setSuccess("")
    const shares = Number(maxSharesInput || 1)
    if (!Number.isFinite(shares) || shares < 1) {
      setError("Max shares must be at least 1.")
      return
    }
    if (!Number.isInteger(shares)) {
      setError("Max shares must be a whole number.")
      return
    }

    setIsSavingMaxShares(true)
    try {
      const result = await onUpdateMaxShares(shares)
      if (result?.ok) {
        setSuccess(result?.message || "Max shares updated.")
        await loadData()
      } else {
        setError(result?.message || "Failed to update max shares.")
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update max shares."))
    } finally {
      setIsSavingMaxShares(false)
    }
  }

  const handleSaveProjectSettings = async () => {
    if (!group?.id || !isProjectSavingsGroup || !isAdmin) return
    setError("")
    setSuccess("")

    const perPersonAmount = Number(projectPerPersonInput || 0)
    if (!Number.isFinite(perPersonAmount) || perPersonAmount <= 0) {
      setError("Per person amount must be greater than 0.")
      return
    }
    if (!projectClosingDateInput) {
      setError("Closing date is required.")
      return
    }

    setIsSavingProjectSettings(true)
    try {
      const res = await api.patch(`/groups/${group.id}/project-savings/settings`, {
        perPersonAmount,
        closingDate: projectClosingDateInput,
      })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setSuccess(res?.data?.message || "Project savings settings saved.")
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to save project savings settings.")
      setError(message)
    } finally {
      setIsSavingProjectSettings(false)
    }
  }

  const handleSaveProjectTargets = async () => {
    if (!group?.id || !isProjectSavingsGroup || !isAdmin) return
    setError("")
    setSuccess("")

    const members = Array.isArray(group?.members) ? group.members : []
    if (!members.length) {
      setError("No active members available.")
      return
    }

    const memberTargets = []
    for (const member of members) {
      const memberId = String(member?.id || "")
      const raw = projectTargetDraftByMember?.[memberId]
      const value = Number(raw)
      if (!Number.isFinite(value) || value < 0) {
        setError("Each member target must be a number greater than or equal to 0.")
        return
      }
      memberTargets.push({ memberId, targetAmount: value })
    }

    setIsSavingProjectTargets(true)
    try {
      const res = await api.patch(`/groups/${group.id}/project-savings/allocations`, {
        mode: "manual",
        memberTargets,
      })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setSuccess(res?.data?.message || "Member targets saved.")
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to save member targets.")
      setError(message)
    } finally {
      setIsSavingProjectTargets(false)
    }
  }

  const handleRemoveProjectMember = async (memberId) => {
    if (!group?.id || !memberId || !isProjectSavingsGroup || !isAdmin) return
    setError("")
    setSuccess("")
    setIsRemovingProjectMemberId(String(memberId))
    try {
      const res = await api.patch(`/groups/${group.id}/project-savings/members/${memberId}`, {
        removeMember: true,
      })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setSuccess(res?.data?.message || "Member removed.")
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to remove member.")
      setError(message)
    } finally {
      setIsRemovingProjectMemberId("")
    }
  }

  const payableInvoices = useMemo(
    () =>
      (invoices || [])
        .filter(
          (invoice) =>
            Number(invoice?.balanceAmount || 0) > 0 &&
            invoice?.status !== "paid" &&
            invoice?.status !== "cancelled"
        )
        .sort((a, b) => new Date(a?.dueDate || 0).getTime() - new Date(b?.dueDate || 0).getTime()),
    [invoices]
  )

  const invoiceOutstandingAmount = useMemo(
    () => payableInvoices.reduce((sum, invoice) => sum + Number(invoice?.balanceAmount || 0), 0),
    [payableInvoices]
  )
  const welfarePaidTotal = useMemo(
    () =>
      (groupWalletTransactions || [])
        .filter((tx) => {
          if (String(tx?.type || "").toLowerCase() !== "member_contribution") return false
          return String(tx?.memberId || "") === String(currentMemberId || "")
        })
        .reduce((sum, tx) => sum + Math.abs(Number(tx?.absoluteAmount ?? tx?.amount ?? 0)), 0),
    [groupWalletTransactions, currentMemberId]
  )
  const welfareBenefitedTotal = useMemo(
    () =>
      (groupWalletTransactions || [])
        .filter((tx) => {
          if (String(tx?.type || "").toLowerCase() !== "payout") return false
          if (isWelfareGroup && !String(tx?.description || "").toLowerCase().includes("loss report")) {
            return false
          }
          return String(tx?.memberId || "") === String(currentMemberId || "")
        })
        .reduce(
          (sum, tx) => sum + Math.abs(Number(tx?.absoluteAmount ?? tx?.amount ?? 0)),
          0
        ),
    [groupWalletTransactions, currentMemberId, isWelfareGroup]
  )
  const welfareBenefitOutstandingAmount = useMemo(
    () => Math.max(0, Number(welfareBenefitedTotal || 0) - Number(welfarePaidTotal || 0)),
    [welfareBenefitedTotal, welfarePaidTotal]
  )
  const outstandingAmount = useMemo(() => {
    if (!isWelfareGroup) return invoiceOutstandingAmount
    return welfareBenefitOutstandingAmount
  }, [isWelfareGroup, invoiceOutstandingAmount, welfareBenefitOutstandingAmount])
  const effectiveInvoiceScope = isChamaaGroup ? "current" : isWelfareGroup ? "all" : invoiceScope

  const orderedGroupInvoices = useMemo(() => {
    const outstandingOnly = (allGroupInvoices || []).filter(
      (invoice) =>
        Number(invoice?.balanceAmount || 0) > 0 &&
        !["paid", "cancelled"].includes(String(invoice?.status || "").toLowerCase())
    )
    const scoped =
      effectiveInvoiceScope === "current"
        ? outstandingOnly.filter((invoice) => {
          const invoiceCycleId = String(invoice?.cycleId || "")
          const invoiceCycleSequence = Number(invoice?.cycleSequence || 0) || null
          if (activeCycleId && invoiceCycleId) return invoiceCycleId === activeCycleId
          return !!activeCycleSequence && invoiceCycleSequence === activeCycleSequence
        })
        : outstandingOnly

    return [...scoped].sort((a, b) => {
      const aIsMine = currentMemberId && String(a?.memberId || "") === currentMemberId
      const bIsMine = currentMemberId && String(b?.memberId || "") === currentMemberId
      if (aIsMine && !bIsMine) return -1
      if (!aIsMine && bIsMine) return 1
      const aDate = new Date(a?.createdAt || a?.issuedAt || a?.dueDate || 0).getTime()
      const bDate = new Date(b?.createdAt || b?.issuedAt || b?.dueDate || 0).getTime()
      return bDate - aDate
    })
  }, [allGroupInvoices, effectiveInvoiceScope, activeCycleId, activeCycleSequence, currentMemberId])
  const noOutstandingMessage =
    isProjectSavingsGroup
      ? "No outstanding invoices yet. Use the Project Savings plan to manage member targets."
      : effectiveInvoiceScope === "current"
        ? activeCycle
          ? "No outstanding invoices in current cycle."
          : "No active cycle available here. Previous-cycle balances are shown in Old Cycles."
        : "No outstanding invoices across cycles."
  const visibleOutstandingInvoices = useMemo(
    () => orderedGroupInvoices.slice(0, outstandingVisibleCount),
    [orderedGroupInvoices, outstandingVisibleCount]
  )
  const canShowMoreOutstandingInvoices = outstandingVisibleCount < orderedGroupInvoices.length
  const oldestOutstandingInvoiceByMember = useMemo(() => {
    const byMember = new Map()
    const sortedInvoices = [...(allGroupInvoices || [])]
      .filter((invoice) => {
        const balanceAmount = Number(invoice?.balanceAmount || 0)
        const status = String(invoice?.status || "").toLowerCase()
        return balanceAmount > 0 && !["paid", "cancelled"].includes(status)
      })
      .sort((a, b) => {
        const aDue = new Date(a?.dueDate || a?.createdAt || 0).getTime()
        const bDue = new Date(b?.dueDate || b?.createdAt || 0).getTime()
        if (aDue !== bDue) return aDue - bDue
        const roundDiff = Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
        if (roundDiff !== 0) return roundDiff
        return new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()
      })

    for (const invoice of sortedInvoices) {
      const memberId = String(invoice?.memberId || "")
      if (!memberId || byMember.has(memberId)) continue
      byMember.set(memberId, invoice)
    }

    return byMember
  }, [allGroupInvoices])
  const outstandingSummary = useMemo(() => {
    const nowMs = Date.now()
    const pendingCount = orderedGroupInvoices.filter(
      (invoice) => resolveInvoiceDisplayStatus(invoice, nowMs) === "pending"
    ).length
    const overdueCount = orderedGroupInvoices.filter(
      (invoice) => resolveInvoiceDisplayStatus(invoice, nowMs) === "overdue"
    ).length
    const totalDueCount = pendingCount + overdueCount
    const totalAmount = orderedGroupInvoices.reduce(
      (sum, invoice) => sum + Number(invoice?.balanceAmount || 0),
      0
    )
    return { pendingCount, overdueCount, totalDueCount, totalAmount }
  }, [orderedGroupInvoices])
  const myWelfareOutstandingInvoicesByOldest = useMemo(() => {
    if (!isWelfareGroup || !currentMemberId) return []
    return [...(allGroupInvoices || [])]
      .filter((invoice) => {
        if (String(invoice?.memberId || "") !== String(currentMemberId || "")) return false
        if (!(Number(invoice?.balanceAmount || 0) > 0)) return false
        const status = String(invoice?.status || "").toLowerCase()
        return !["paid", "cancelled"].includes(status)
      })
      .sort((a, b) => {
        const aDue = new Date(a?.dueDate || a?.createdAt || 0).getTime()
        const bDue = new Date(b?.dueDate || b?.createdAt || 0).getTime()
        if (aDue !== bDue) return aDue - bDue
        const roundDiff = Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
        if (roundDiff !== 0) return roundDiff
        return new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()
      })
  }, [allGroupInvoices, currentMemberId, isWelfareGroup])
  const myWelfareOutstandingAmount = useMemo(
    () =>
      myWelfareOutstandingInvoicesByOldest.reduce(
        (sum, invoice) => sum + Number(invoice?.balanceAmount || 0),
        0
      ),
    [myWelfareOutstandingInvoicesByOldest]
  )
  const groupWalletBalanceValue = Number(ledgerGroupWalletBalance ?? group?.groupWalletBalance ?? 0)
  const projectTargetRows = useMemo(() => {
    if (!isProjectSavingsGroup) return []
    return (group?.members || [])
      .map((member) => {
        const targetAmount = Number(
          projectTargetDraftByMember?.[String(member?.id || "")] ??
          member?.projectSavingsTargetAmount ??
          member?.expected ??
          0
        )
        const paidAmount = Number(member?.paid || 0)
        return {
          id: String(member?.id || ""),
          name: member?.name || "Member",
          role: member?.role || "member",
          targetAmount: Number.isFinite(targetAmount) ? targetAmount : 0,
          paidAmount,
          remainingAmount: Math.max(0, (Number.isFinite(targetAmount) ? targetAmount : 0) - paidAmount),
        }
      })
      .sort((a, b) => {
        const aIsMine = currentMemberId && String(a?.id || "") === currentMemberId
        const bIsMine = currentMemberId && String(b?.id || "") === currentMemberId
        if (aIsMine && !bIsMine) return -1
        if (!aIsMine && bIsMine) return 1
        return String(a?.name || "").localeCompare(String(b?.name || ""))
      })
  }, [group?.members, isProjectSavingsGroup, projectTargetDraftByMember, currentMemberId])
  const totalProjectMembers = projectTargetRows.length
  const visibleProjectTargetRows = useMemo(
    () => projectTargetRows.slice(0, projectMembersVisibleCount),
    [projectMembersVisibleCount, projectTargetRows]
  )
  const isProjectMembersTableScrollable = visibleProjectTargetRows.length >= 10
  const walletLedgerRows = useMemo(() => {
    if (isWelfareGroup) {
      const paymentMetaById = new Map(
        (allMemberPayments || [])
          .map((payment) => [
            String(payment?.id || ""),
            {
              referenceCode: String(payment?.referenceCode || "").trim(),
              invoiceNotes: String(payment?.invoiceNotes || "").trim(),
            },
          ])
          .filter(([paymentId]) => !!paymentId)
      )

      const rows = (groupWalletTransactions || [])
        .filter((tx) => {
          const txType = String(tx?.type || "").toLowerCase()
          if (txType === "member_contribution") return true
          if (txType === "payout") {
            return String(tx?.description || "").toLowerCase().includes("loss report")
          }
          return txType === "adjustment"
        })
        .map((tx) => {
          const txType = String(tx?.type || "").toLowerCase()
          const normalizedDescription = normalizeTransactionDescription(tx?.description || "")
          const loweredDescription = normalizedDescription.toLowerCase()
          const isAdminBill =
            txType === "adjustment" && loweredDescription.startsWith("admin bill:")
          const linkedPaymentMeta =
            paymentMetaById.get(String(tx?.groupPaymentId || "")) || null
          const relationLabel =
            extractRelationFromText(normalizedDescription) ||
            extractRelationFromText(linkedPaymentMeta?.invoiceNotes || "")
          const rawReferenceCode = String(
            tx?.referenceCode || linkedPaymentMeta?.referenceCode || ""
          ).trim()
          const isLossReport = loweredDescription.includes("loss report")
          const isRegistration = loweredDescription.includes("registration")
          const isReimbursement =
            loweredDescription.includes("reimbursement") ||
            (!!relationLabel && !isRegistration)
          const defaultReferenceCode = isRegistration ? "REG" : rawReferenceCode

          let description = normalizedDescription || "Transaction"
          let amount = Number(tx?.amount || 0)
          let entryType = txType || "transaction"

          if (txType === "member_contribution") {
            amount = Math.abs(Number(tx?.absoluteAmount ?? tx?.amount ?? 0))
            if (isRegistration) {
              description = "OUT - Reg fee"
              entryType = "registration_fee"
            } else if (isReimbursement) {
              description = relationLabel
                ? `OUT - Reimbursement (${relationLabel})`
                : "OUT - Reimbursement"
              entryType = "reimbursement"
            } else {
              description = "OUT - Payment"
              entryType = "payment"
            }
          } else if (txType === "payout") {
            amount = -Math.abs(Number(tx?.absoluteAmount ?? tx?.amount ?? 0))
            description = relationLabel
              ? `IN - Loss report (${relationLabel})`
              : "IN - Loss report"
            entryType = isLossReport ? "benefit_loss_report" : "payout"
          } else if (txType === "adjustment") {
            amount = Number(tx?.amount || 0)
            description = normalizedDescription || "Adjustment"
            entryType = isAdminBill ? "group_expense" : "adjustment"
          }

          return {
            id: `wallet-${txType}-${tx?.id || tx?._id || "na"}-${tx?.timestamp || "na"}`,
            memberId: tx?.memberId || null,
            memberName: tx?.memberName || "",
            welfareMemberNumber: Number(tx?.welfareMemberNumber || 0) || null,
            welfareMemberIdentifier: String(tx?.welfareMemberIdentifier || "").trim(),
            date: tx?.timestamp || null,
            cycleSequence: null,
            roundNumber: null,
            referenceCode: defaultReferenceCode,
            description,
            amount,
            balanceBefore: tx?.balanceBefore,
            balanceAfter: tx?.balanceAfter,
            entryType,
          }
        })
        .sort((a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime())

      return applyWalletRowTotals(rows, groupWalletBalanceValue)
    }

    const invoiceById = new Map(
      (allGroupInvoices || [])
        .map((invoice) => [String(invoice?.id || ""), invoice])
        .filter(([invoiceId]) => !!invoiceId)
    )
    const contributionTransactionByPaymentId = new Map(
      (groupWalletTransactions || [])
        .filter((tx) => String(tx?.type || "").toLowerCase() === "member_contribution")
        .map((tx) => [String(tx?.groupPaymentId || ""), tx])
        .filter(([paymentId]) => !!paymentId)
    )

    const paymentRows = (allMemberPayments || []).map((payment) => {
      const cycleSequence = isChamaaGroup ? Number(payment?.cycleSequence || 0) || null : null
      const roundNumber = isChamaaGroup ? Number(payment?.roundNumber || 0) || null : null
      const paymentId = String(payment?.id || "")
      const paymentInvoiceId = String(payment?.invoiceId || "")
      const paymentInvoice = invoiceById.get(paymentInvoiceId) || null
      const referenceCode = String(payment?.referenceCode || paymentInvoice?.referenceCode || "").trim()
      const invoiceNotes = String(payment?.invoiceNotes || paymentInvoice?.notes || "").trim()
      const linkedWalletTransaction = contributionTransactionByPaymentId.get(paymentId) || null
      const fallbackDescription =
        isChamaaGroup && (cycleSequence || roundNumber)
          ? `IN - Contribution (C${cycleSequence || "-"}/R${roundNumber || "-"}, S?)`
          : "IN - Contribution"
      const resolvedDescription =
        String(linkedWalletTransaction?.description || "") || fallbackDescription
      const relationLabel = extractRelationFromText(invoiceNotes)
      const welfareDescription = !referenceCode
        ? "OUT - Reg fee"
        : relationLabel
          ? `OUT - Reimbursement (${relationLabel})`
          : "OUT - Payment"
      return {
        id: `pay-${payment.id}`,
        memberId: payment?.memberId || null,
        memberName: payment?.memberName || "",
        welfareMemberNumber: Number(payment?.welfareMemberNumber || 0) || null,
        welfareMemberIdentifier: String(payment?.welfareMemberIdentifier || "").trim(),
        date: payment?.paidAt || payment?.createdAt || null,
        cycleSequence,
        roundNumber,
        referenceCode,
        description: normalizeTransactionDescription(
          isWelfareGroup ? welfareDescription : resolvedDescription
        ),
        amount: Math.abs(Number(payment?.amount || 0)),
        balanceBefore: linkedWalletTransaction?.balanceBefore,
        balanceAfter: linkedWalletTransaction?.balanceAfter,
        entryType: "contribution",
      }
    })

    const paymentInvoiceIds = new Set(
      (allMemberPayments || [])
        .map((payment) => String(payment?.invoiceId || ""))
        .filter(Boolean)
    )
    const syntheticPaymentRows = (allGroupInvoices || [])
      .filter((invoice) => {
        const status = String(invoice?.status || "").toLowerCase()
        if (status !== "paid" && status !== "partial") return false
        const paidAmount = Number(invoice?.paidAmount || 0)
        if (!(paidAmount > 0)) return false
        const invoiceId = String(invoice?.id || "")
        if (!invoiceId) return false
        return !paymentInvoiceIds.has(invoiceId)
      })
      .map((invoice) => {
        const cycleSequence = isChamaaGroup ? Number(invoice?.cycleSequence || 0) || null : null
        const roundNumber = isChamaaGroup ? Number(invoice?.roundNumber || 0) || null : null
        const referenceCode = String(invoice?.referenceCode || "").trim()
        const invoiceNotes = String(invoice?.notes || "").trim()
        const relationLabel = extractRelationFromText(invoiceNotes)
        const isWelfareRegistration = referenceCode.length === 0
        const welfareDescription = isWelfareRegistration
          ? "OUT - Reg fee"
          : relationLabel
            ? `OUT - Reimbursement (${relationLabel})`
            : "OUT - Payment"
        return {
          id: `invoice-paid-${invoice.id}`,
          memberId: invoice?.memberId || null,
          memberName: invoice?.memberName || "",
          welfareMemberNumber: Number(invoice?.welfareMemberNumber || 0) || null,
          welfareMemberIdentifier: String(invoice?.welfareMemberIdentifier || "").trim(),
          date: invoice?.paidAt || invoice?.createdAt || null,
          cycleSequence,
          roundNumber,
          referenceCode,
          description: normalizeTransactionDescription(
            isWelfareGroup ? welfareDescription : isWelfareRegistration ? "IN - Registration Fee" : "IN - Contribution"
          ),
          amount: Math.abs(Number(invoice?.paidAmount || 0)),
          balanceBefore: null,
          balanceAfter: null,
          entryType: isWelfareRegistration ? "registration_fee" : "contribution",
        }
      })

    const payoutRows = (groupWalletTransactions || [])
      .filter((tx) => String(tx?.type || "").toLowerCase() === "payout")
      .filter((tx) => {
        if (!isWelfareGroup) return true
        return String(tx?.description || "").toLowerCase().includes("loss report")
      })
      .map((tx) => {
        const cycleSequence = isChamaaGroup ? Number(tx?.cycleSequence || 0) || null : null
        const roundNumber = isChamaaGroup ? Number(tx?.roundNumber || 0) || null : null
        const description = String(tx?.description || "")
        const normalizedDescription = normalizeTransactionDescription(description)
        const isLossReportBenefit = normalizedDescription.toLowerCase().includes("loss report")
        const relationLabel = extractRelationFromText(normalizedDescription)
        const welfarePayoutDescription = isLossReportBenefit
          ? relationLabel
            ? `IN - Loss report (${relationLabel})`
            : "IN - Loss report"
          : "IN - Payout"
        return {
          id: `payout-${tx.id}`,
          memberId: tx?.memberId || null,
          memberName: tx?.memberName || "",
          welfareMemberNumber: Number(tx?.welfareMemberNumber || 0) || null,
          welfareMemberIdentifier: String(tx?.welfareMemberIdentifier || "").trim(),
          date: tx?.timestamp || null,
          cycleSequence,
          roundNumber,
          referenceCode: String(tx?.referenceCode || "").trim(),
          description:
            (isWelfareGroup ? welfarePayoutDescription : normalizedDescription) ||
            (isChamaaGroup && (cycleSequence || roundNumber)
              ? `OUT - Payout (C${cycleSequence || "-"}/R${roundNumber || "-"}, Late)`
              : "OUT - Payout"),
          amount: -Math.abs(Number(tx?.absoluteAmount ?? tx?.amount ?? 0)),
          balanceBefore: tx?.balanceBefore,
          balanceAfter: tx?.balanceAfter,
          entryType: isLossReportBenefit ? "benefit_loss_report" : "payout",
        }
      })

    const adjustmentRows = (groupWalletTransactions || [])
      .filter((tx) => String(tx?.type || "").toLowerCase() === "adjustment")
      .map((tx) => ({
        id: `adjustment-${tx.id}`,
        memberId: tx?.memberId || null,
        memberName: tx?.memberName || "",
        welfareMemberNumber: Number(tx?.welfareMemberNumber || 0) || null,
        welfareMemberIdentifier: String(tx?.welfareMemberIdentifier || "").trim(),
        date: tx?.timestamp || null,
        cycleSequence: null,
        roundNumber: null,
        referenceCode: "",
        description: normalizeTransactionDescription(tx?.description || "Adjustment"),
        amount: Number(tx?.amount || 0),
        balanceBefore: tx?.balanceBefore,
        balanceAfter: tx?.balanceAfter,
        entryType: "adjustment",
      }))

    const rows = [...paymentRows, ...syntheticPaymentRows, ...payoutRows, ...adjustmentRows].sort(
      (a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime()
    )

    return applyWalletRowTotals(rows, groupWalletBalanceValue)
  }, [
    allGroupInvoices,
    allMemberPayments,
    groupWalletBalanceValue,
    groupWalletTransactions,
    isChamaaGroup,
    isWelfareGroup,
    normalizeTransactionDescription,
  ])

  const closeAddBillModal = () => {
    if (isAddingBill) return
    setIsAddBillModalOpen(false)
    setBillAmountInput("")
    setBillDescriptionInput("")
  }

  const openWalletTransactionsModal = () => {
    if (isWelfareGroup) {
      const { startDate, endDate } = getLastTwoMonthsDateRange()
      setWalletTransactionsStartDate(startDate)
      setWalletTransactionsEndDate(endDate)
      setWalletTransactionsAppliedStartDate(startDate)
      setWalletTransactionsAppliedEndDate(endDate)
    } else {
      setWalletTransactionsStartDate("")
      setWalletTransactionsEndDate("")
      setWalletTransactionsAppliedStartDate("")
      setWalletTransactionsAppliedEndDate("")
    }
    setWalletTransactionsFilterOpen(false)
    setIsWalletTransactionsOpen(true)
  }

  const closeWalletTransactionsModal = () => {
    setIsWalletTransactionsOpen(false)
  }

  const applyWalletTransactionsDateFilter = () => {
    setWalletTransactionsAppliedStartDate(walletTransactionsStartDate)
    setWalletTransactionsAppliedEndDate(walletTransactionsEndDate)
  }

  const clearWalletTransactionsDateFilter = () => {
    setWalletTransactionsStartDate("")
    setWalletTransactionsEndDate("")
    setWalletTransactionsAppliedStartDate("")
    setWalletTransactionsAppliedEndDate("")
  }

  const handleAddBill = async () => {
    if (!group?.id || !isAdmin) return
    if (addBillValidationMessage) {
      setError(addBillValidationMessage)
      return
    }
    setError("")
    setSuccess("")
    setIsAddingBill(true)
    try {
      const amount = Number(billAmountInput || 0)
      const description = String(billDescriptionInput || "").trim()
      const res = await api.post(`/groups/${group.id}/admin/wallet/bills`, { amount, description })
      setSuccess(res?.data?.message || "Bill added successfully.")
      setIsAddBillModalOpen(false)
      setBillAmountInput("")
      setBillDescriptionInput("")
      await loadData()
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to add bill.")
      setError(message)
    } finally {
      setIsAddingBill(false)
    }
  }

  const walletCycleOptions = useMemo(() => {
    const set = new Set(
      walletLedgerRows
        .map((row) => Number(row?.cycleSequence || 0))
        .filter((value) => Number.isFinite(value) && value > 0)
    )
    return Array.from(set).sort((a, b) => b - a)
  }, [walletLedgerRows])

  const walletLedgerRowsDateFiltered = useMemo(
    () =>
      filterRowsByDateRange(
        walletLedgerRows,
        walletTransactionsAppliedStartDate,
        walletTransactionsAppliedEndDate,
        (row) => row?.date
      ),
    [
      walletLedgerRows,
      walletTransactionsAppliedStartDate,
      walletTransactionsAppliedEndDate,
    ]
  )

  const welfareDateFilteredTotals = useMemo(() => {
    if (!isWelfareGroup) {
      return { paidTotal: 0, benefitedTotal: 0, groupExpensesTotal: 0, transactionsTotal: 0 }
    }

    const paidTotal = walletLedgerRowsDateFiltered.reduce((sum, row) => {
      const entryType = String(row?.entryType || "").toLowerCase()
      const amount = Math.abs(Number(row?.amount || 0))
      if (!(amount > 0)) return sum
      return ["payment", "registration_fee", "reimbursement"].includes(entryType)
        ? sum + amount
        : sum
    }, 0)

    const benefitedTotal = walletLedgerRowsDateFiltered.reduce((sum, row) => {
      const entryType = String(row?.entryType || "").toLowerCase()
      const amount = Math.abs(Number(row?.amount || 0))
      if (!(amount > 0)) return sum
      return entryType === "benefit_loss_report" ? sum + amount : sum
    }, 0)

    const groupExpensesTotal = walletLedgerRowsDateFiltered.reduce((sum, row) => {
      const entryType = String(row?.entryType || "").toLowerCase()
      const amount = Math.abs(Number(row?.amount || 0))
      if (!(amount > 0)) return sum
      return entryType === "group_expense" ? sum + amount : sum
    }, 0)

    return {
      paidTotal,
      benefitedTotal,
      groupExpensesTotal,
      transactionsTotal: paidTotal + benefitedTotal + groupExpensesTotal,
    }
  }, [isWelfareGroup, walletLedgerRowsDateFiltered])

  const filteredWalletLedgerRows = useMemo(() => {
    const q = String(walletTransactionsSearch || "").trim().toLowerCase()
    const selectedCycle = String(walletTransactionsCycleFilter || "all")
    return walletLedgerRowsDateFiltered.filter((row) => {
      const matchesName = !q || String(row?.memberName || "").toLowerCase().includes(q)
      const matchesCycle =
        !isChamaaGroup || selectedCycle === "all"
          ? true
          : String(row?.cycleSequence || "") === selectedCycle
      return matchesName && matchesCycle
    })
  }, [
    isChamaaGroup,
    walletLedgerRowsDateFiltered,
    walletTransactionsSearch,
    walletTransactionsCycleFilter,
  ])
  const myOutstandingLedgerRows = useMemo(() => {
    const memberId = String(currentMemberId || "")
    if (!memberId) return []
    const rows = walletLedgerRows.filter((row) => String(row?.memberId || "") === memberId)
    const chronologicalRows = [...rows].sort((a, b) => {
      const aTime = new Date(a?.date || 0).getTime()
      const bTime = new Date(b?.date || 0).getTime()
      if (aTime !== bTime) return aTime - bTime
      return String(a?.id || "").localeCompare(String(b?.id || ""))
    })
    const runningTotalById = new Map()
    let runningTotal = 0
    for (const row of chronologicalRows) {
      runningTotal += Number(row?.amount || 0)
      runningTotalById.set(String(row?.id || ""), runningTotal)
    }
    return rows.map((row) => ({
      ...row,
      total: Number(runningTotalById.get(String(row?.id || "")) || 0),
    }))
  }, [currentMemberId, walletLedgerRows])
  const myCurrentTransactionTotal = useMemo(
    () => Number(myOutstandingLedgerRows?.[0]?.total || 0),
    [myOutstandingLedgerRows]
  )

  const currentCycleDueByMember = useMemo(() => {
    if (!isChamaaGroup) return new Map()
    const dueByMember = new Map()

    for (const invoice of allGroupInvoices || []) {
      const balanceAmount = Number(invoice?.balanceAmount || 0)
      if (!(balanceAmount > 0)) continue

      const status = String(invoice?.status || "").toLowerCase()
      if (status === "paid" || status === "cancelled") continue

      const invoiceCycleId = String(invoice?.cycleId || "")
      const invoiceCycleSequence = Number(invoice?.cycleSequence || 0) || null
      const matchesActiveCycle =
        activeCycleId && invoiceCycleId
          ? invoiceCycleId === activeCycleId
          : !!activeCycleSequence && invoiceCycleSequence === activeCycleSequence

      if (!matchesActiveCycle) continue

      const memberId = String(invoice?.memberId || "")
      if (!memberId) continue
      dueByMember.set(memberId, Number(dueByMember.get(memberId) || 0) + balanceAmount)
    }

    return dueByMember
  }, [allGroupInvoices, isChamaaGroup, activeCycleId, activeCycleSequence])

  const currentCycleOutstandingInvoicesByMember = useMemo(() => {
    const byMember = new Map()
    if (!isChamaaGroup) return byMember

    const currentCycleInvoices = (allGroupInvoices || [])
      .filter((invoice) => {
        const balanceAmount = Number(invoice?.balanceAmount || 0)
        if (!(balanceAmount > 0)) return false
        const status = String(invoice?.status || "").toLowerCase()
        if (status === "paid" || status === "cancelled") return false

        const invoiceCycleId = String(invoice?.cycleId || "")
        const invoiceCycleSequence = Number(invoice?.cycleSequence || 0) || null
        return activeCycleId && invoiceCycleId
          ? invoiceCycleId === activeCycleId
          : !!activeCycleSequence && invoiceCycleSequence === activeCycleSequence
      })
      .sort((a, b) => {
        const roundDiff = Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
        if (roundDiff !== 0) return roundDiff
        const aDate = new Date(a?.dueDate || a?.createdAt || 0).getTime()
        const bDate = new Date(b?.dueDate || b?.createdAt || 0).getTime()
        return aDate - bDate
      })

    for (const invoice of currentCycleInvoices) {
      const memberId = String(invoice?.memberId || "")
      if (!memberId) continue
      if (!byMember.has(memberId)) byMember.set(memberId, [])
      byMember.get(memberId).push(invoice)
    }

    return byMember
  }, [activeCycleId, activeCycleSequence, allGroupInvoices, isChamaaGroup])

  const cycleMembers = useMemo(() => {
    if (!isChamaaGroup || !Array.isArray(activeCycle?.participants)) return []
    const byId = new Map((group?.members || []).map((member) => [String(member.id), member]))
    return activeCycle.participants
      .map((participant) => {
        const memberId = String(participant?.id || "")
        const source = byId.get(memberId) || null
        const outstandingInvoices = currentCycleOutstandingInvoicesByMember.get(memberId) || []
        return {
          id: memberId,
          name: participant?.name || source?.name || "Member",
          role: source?.role || "member",
          membershipStatus: source?.membershipStatus || "in-cycle",
          shareCount: Number(participant?.shareCount || source?.desiredShares || 1),
          wonShares: Number(participant?.wonShares || 0),
          alreadyWon: !!participant?.alreadyWon,
          wonRound: participant?.wonRound || null,
          totalWonAmount: Number(participant?.totalWonAmount || 0),
          dueAmount: Number(currentCycleDueByMember.get(memberId) || 0),
          outstandingInvoiceCount: outstandingInvoices.length,
        }
      })
      .sort((a, b) => {
        const aIsMine = currentMemberId && String(a?.id || "") === currentMemberId
        const bIsMine = currentMemberId && String(b?.id || "") === currentMemberId
        if (aIsMine && !bIsMine) return -1
        if (!aIsMine && bIsMine) return 1
        return String(a?.name || "").localeCompare(String(b?.name || ""))
      })
  }, [
    activeCycle?.participants,
    currentCycleDueByMember,
    currentCycleOutstandingInvoicesByMember,
    group?.members,
    isChamaaGroup,
    currentMemberId,
  ])

  const cycleMemberStats = useMemo(() => {
    const memberCount = cycleMembers.length
    const totalShares = cycleMembers.reduce((sum, member) => sum + Number(member?.shareCount || 0), 0)
    const recipientCount = cycleMembers.filter((member) => member?.alreadyWon).length
    const totalDue = cycleMembers.reduce((sum, member) => sum + Number(member?.dueAmount || 0), 0)
    return { memberCount, totalShares, recipientCount, totalDue }
  }, [cycleMembers])

  const filteredCycleMembers = useMemo(() => {
    const term = String(cycleMembersSearch || "").trim().toLowerCase()
    if (!term) return cycleMembers
    return cycleMembers.filter((member) => String(member?.name || "").toLowerCase().includes(term))
  }, [cycleMembers, cycleMembersSearch])

  const visibleCycleMembers = useMemo(
    () => filteredCycleMembers.slice(0, cycleMembersVisibleCount),
    [filteredCycleMembers, cycleMembersVisibleCount]
  )

  useEffect(() => {
    setCycleMembersVisibleCount(5)
  }, [cycleMembersSearch, activeCycle?.id, cycleMembers.length])

  useEffect(() => {
    setProjectMembersVisibleCount(5)
  }, [group?.id, projectTargetRows.length])

  useEffect(() => {
    setOutstandingVisibleCount(5)
  }, [effectiveInvoiceScope, group?.id, orderedGroupInvoices.length])

  useEffect(() => {
    setIsMyOutstandingOpen(false)
  }, [group?.id])

  return (
    <div className="space-y-2">
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Archived view is read-only. Reactivate the group to post payments or update finance settings.
        </div>
      )}
      <fieldset
        disabled={readOnly}
        className={`min-w-0 border-0 p-0 m-0 ${readOnly ? "opacity-75" : ""}`}
      >
    <div className="space-y-4">
      {/* Wallet Summary Grid */}
      <div className={`grid gap-2 ${isChamaaGroup ? "grid-cols-2" : "grid-cols-3"}`}>
        {/* Group Wallet */}
        <button
          type="button"
          onClick={openWalletTransactionsModal}
          className={`bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center transition hover:bg-gray-50 group border-b-2 border-transparent ${theme.borderLeft.replace('border-l-', 'hover:border-')}`}
        >
          <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1 group-hover:${theme.border} transition`}>
            <Wallet size={16} />
          </div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight">Group Transactions</p>
          <p className="text-xs font-bold text-gray-900 mt-0.5">{formatMoney(groupWalletBalanceValue)}</p>
        </button>

        {/* My RLogs Wallet */}
        <div className="bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center">
          <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1`}>
            <Wallet size={16} />
          </div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight">{myWalletBalanceLabel}</p>
          <p className={`text-xs font-bold ${theme.text} mt-0.5`}>{formatMoney(walletBalance)}</p>
        </div>

        {/* My Transactions / Outstanding */}
        <button
          type="button"
          onClick={() => setIsMyOutstandingOpen(true)}
          className={`bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center transition hover:bg-gray-50 group border-b-2 border-transparent ${theme.borderLeft.replace('border-l-', 'hover:border-')}`}
        >
          <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1`}>
            <Info size={16} />
          </div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight">{myTransactionsLabel}</p>
          <p className={`text-xs font-bold mt-0.5 ${outstandingAmount > 0 ? "text-red-600" : "text-gray-900"}`}>
            {formatMoney(outstandingAmount)}
          </p>
        </button>

        {/* Enrollment */}
        {isChamaaGroup && (
          <button
            type="button"
            onClick={() => setIsEnrollmentSectionOpen(!isEnrollmentSectionOpen)}
            className={`p-2.5 rounded-xl border flex flex-col items-center text-center transition group border-b-2 border-transparent ${theme.borderLeft.replace('border-l-', 'hover:border-')} ${
              isEnrollmentSectionOpen
                ? `${theme.bg} ${theme.border}`
                : "bg-white border-gray-100 hover:bg-gray-50 shadow-sm"
            }`}
          >
            <div className={`p-1.5 rounded-lg mb-1 transition ${
              isEnrollmentSectionOpen ? `${theme.border} ${theme.text}` : `${theme.bg} ${theme.text} group-hover:${theme.border}`
            }`}>
              <PlusCircle size={16} />
            </div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight">Shares</p>
            <p className="text-[8px] text-gray-400 mt-0.5 leading-tight px-0.5">Manage your shares</p>
          </button>
        )}
      </div>

      {!isChamaaGroup && (
        <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
            {isWelfareGroup ? (
              <>
                <span className={`px-2 py-1 rounded-lg border ${welfareStatusPillClass}`}>
                  Status: <span className="font-semibold">{welfareStatusLabel}</span>
                </span>
                {welfareProbationDisplay?.isOnProbation && (
                  <>
                    <span className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700">
                      {welfareProbationDisplay?.scenarioLabel || "Probation"}
                    </span>
                    <span className="px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-700">
                      Start:{" "}
                      <span className="font-semibold">
                        {welfareProbationDisplay?.startAt
                          ? formatDateLong(welfareProbationDisplay.startAt)
                          : "-"}
                      </span>
                    </span>
                    <span className="px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-700">
                      End:{" "}
                      <span className="font-semibold">
                        {welfareProbationDisplay?.endAt
                          ? formatDateLong(welfareProbationDisplay.endAt)
                          : "-"}
                      </span>
                    </span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="bg-gray-50 border border-gray-100 px-2 py-1 rounded-lg">
                  Type: <span className="font-semibold">{memberInfo?.membershipStatus || "-"}</span>
                </span>
                <span className="bg-gray-50 border border-gray-100 px-2 py-1 rounded-lg">
                  Role: <span className="font-semibold">{memberInfo?.role || "-"}</span>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {isChamaaGroup && !canPayInvoices && (
        <p className="text-[10px] text-amber-700">
          Invoice payments are enabled for group members.
        </p>
      )}

      {isProjectSavingsGroup && (
        <div className={`bg-white/95 rounded-2xl border ${theme.border} shadow-md p-4 space-y-4`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Savings Target and Total Saved</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Total members: <span className="font-semibold text-gray-700">{totalProjectMembers}</span>
              </p>
            </div>
          </div>

          {isAdmin && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-wide ${theme.text} mb-1`}>
                    Per Person
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={projectPerPersonInput}
                    onChange={(e) => handleProjectPerPersonInputChange(e.target.value)}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none bg-white`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-wide ${theme.text} mb-1`}>
                    Total Target
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={projectTotalTargetInput}
                    onChange={(e) => handleProjectTotalTargetInputChange(e.target.value)}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none bg-white`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-wide ${theme.text} mb-1`}>
                    Closing Date
                  </label>
                  <input
                    type="date"
                    value={projectClosingDateInput}
                    onChange={(e) => setProjectClosingDateInput(e.target.value)}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none bg-white`}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={handleSaveProjectSettings}
                    disabled={isSavingProjectSettings}
                    className={`px-3 py-2 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide transition-all shadow-sm ${theme.button} disabled:opacity-60`}
                  >
                    {isSavingProjectSettings ? "Saving..." : "Update For All"}
                  </button>
                </div>
              </div>
            </>
          )
          }

          {projectTargetRows.length === 0 ? (
            <p className="text-xs text-gray-500">No active members found.</p>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className={isProjectMembersTableScrollable ? "max-h-[22rem] overflow-y-auto" : ""}>
                  <table className="w-full table-auto sm:table-fixed text-left border-collapse text-[8px] sm:text-[10px]">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                      <tr>
                        <th className="px-0.5 py-2 sm:w-[30%] sm:px-2">Member</th>
                        <th className="pl-0.5 pr-0 py-2 sm:w-[22%] sm:px-2">Target</th>
                        <th className="pl-0 pr-0.5 py-2 sm:w-[16%] sm:px-2 text-right">
                          <span className="hidden sm:inline">Total paid</span>
                          <span className="sm:hidden">Paid</span>
                        </th>
                        <th className="px-0.5 py-2 sm:w-[16%] sm:px-2 text-right">
                          Balance
                        </th>
                        {isAdmin && (
                          <th className="px-0.5 py-2 sm:w-[14%] sm:px-2 text-right">
                            <span className="hidden sm:inline">Action</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600 bg-white">
                      {visibleProjectTargetRows.map((row) => {
                        const isRowAdmin =
                          String(row?.role || "").toLowerCase() === "admin" ||
                          String(group?.adminId || "") === String(row.id)
                        return (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-0.5 py-2 sm:px-2">
                              <span className="font-medium text-gray-900 block leading-tight whitespace-normal break-words">
                                {row.name}
                              </span>
                              {isRowAdmin && (
                                <span className={`text-[8px] font-semibold ${theme.text}`}>
                                  (Admin)
                                </span>
                              )}
                            </td>
                            <td className="pl-0.5 pr-0 py-2 sm:px-2">
                              {isAdmin ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={projectTargetDraftByMember?.[row.id] ?? String(row.targetAmount || 0)}
                                  onChange={(e) =>
                                    setProjectTargetDraftByMember((prev) => ({
                                      ...prev,
                                      [row.id]: e.target.value,
                                    }))
                                  }
                                  className={`w-full max-w-[64px] sm:max-w-[92px] px-1 py-1 border rounded text-[8px] sm:text-[10px] outline-none focus:ring-1 ${theme.border} ${theme.ring}`}
                                />
                              ) : (
                                <span className="font-medium text-gray-900">{formatMoney(row.targetAmount || 0)}</span>
                              )}
                            </td>
                            <td className="pl-0 pr-0.5 py-2 sm:px-2 text-right text-emerald-700 font-medium whitespace-nowrap">
                              {formatMoney(row.paidAmount || 0)}
                            </td>
                            <td className="px-1.5 py-2 sm:px-2 text-right text-rose-700 font-medium whitespace-nowrap">
                              {formatMoney(row.remainingAmount || 0)}
                            </td>
                            {isAdmin && (
                              <td className="px-1.5 py-2 sm:px-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveProjectMember(row.id)}
                                  disabled={isRowAdmin || String(isRemovingProjectMemberId) === String(row.id)}
                                  className="px-1 py-1 sm:px-1.5 rounded text-[7px] sm:text-[8px] leading-tight whitespace-nowrap font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                                >
                                  {String(isRemovingProjectMemberId) === String(row.id)
                                    ? "Removing..."
                                    : isRowAdmin
                                      ? "Admin"
                                      : "Remove"}
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] text-gray-500">
                  Showing {visibleProjectTargetRows.length} of {totalProjectMembers} members
                </p>
                {projectTargetRows.length > visibleProjectTargetRows.length && (
                  <button
                    type="button"
                    onClick={() => setProjectMembersVisibleCount((prev) => prev + 5)}
                    className={`px-3 py-1.5 rounded text-[10px] font-semibold text-white transition-all ${theme.button}`}
                  >
                    View More
                  </button>
                )}
              </div>
              {isAdmin && (
                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={handleSaveProjectTargets}
                    disabled={isSavingProjectTargets}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide disabled:opacity-60 transition-all shadow-sm ${theme.button}`}
                  >
                    {isSavingProjectTargets ? "Saving..." : "Save Individual Targets"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isChamaaGroup && isEnrollmentSectionOpen && (
        <div className={`bg-white/95 rounded-xl border shadow-sm p-3 animate-in fade-in slide-in-from-top-2 duration-300 ${theme.border}`}>
          {!!enrollmentSharesBlockedMessage && (
            <div className="mb-2 rounded-lg border border-amber-100 bg-amber-50/50 px-2 py-1.5 text-[10px] text-amber-800 flex items-center gap-1.5">
              <Info size={12} className="flex-shrink-0" />
              <span>{enrollmentSharesBlockedMessage}</span>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {/* Max Shares (Admin only) */}
            {isAdmin && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className={`text-[10px] font-bold uppercase tracking-wide ${theme.text}`}>
                    Max Shares per Person
                  </label>
                </div>
                {isEnrollmentOpen ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={maxSharesInput}
                      onChange={(e) => setMaxSharesInput(e.target.value)}
                      className={`w-full px-2 py-1 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                    />
                    <button
                      type="button"
                      onClick={handleUpdateMaxShares}
                      disabled={isSavingMaxShares}
                      className={`px-3 py-1 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide disabled:opacity-60 transition-colors shadow-sm whitespace-nowrap ${theme.button}`}
                    >
                      {isSavingMaxShares ? "..." : "Save"}
                    </button>
                  </div>
                ) : (
                  <div className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-900">{group?.maxSharesPerMember || 1}</span>
                    <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter bg-white px-1.5 py-0.5 rounded border border-gray-100 shadow-sm">
                      Locked
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* My Shares */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <label className={`text-[10px] font-bold uppercase tracking-wide ${theme.text}`}>
                  My Shares
                </label>
                {!isAdmin && (
                  <p className="text-[9px] text-gray-400">
                    Max allowed: <span className="font-bold text-gray-600">{group?.maxSharesPerMember || 1}</span>
                  </p>
                )}
              </div>
              
              {isEnrollmentOpen ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max={group?.maxSharesPerMember || 1}
                    step="1"
                    value={desiredSharesInput}
                    onChange={(e) => setDesiredSharesInput(e.target.value)}
                    className={`w-full px-2 py-1 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                  />
                  <button
                    type="button"
                    onClick={handleUpdateDesiredShares}
                    disabled={isSavingDesiredShares}
                    className={`px-3 py-1 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide disabled:opacity-60 transition-colors shadow-sm whitespace-nowrap ${theme.button}`}
                  >
                    {isSavingDesiredShares ? "..." : "Save"}
                  </button>
                </div>
              ) : (
                <div className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-900">{currentDesiredShares}</span>
                  <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter bg-white px-1.5 py-0.5 rounded border border-gray-100 shadow-sm">
                    Locked
                  </span>
                </div>
              )}
              {isAdmin && isEnrollmentOpen && (
                <p className="text-[9px] text-gray-400 mt-1">
                  Limit: <span className="font-bold text-gray-600">{group?.maxSharesPerMember || 1}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isChamaaGroup && (
        <div className={`bg-white/95 rounded-2xl border ${theme.border} shadow-md p-4`}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Members In Current Cycle</h4>
              <p className="text-xs text-gray-500 mt-1">
                Everyone enrolled in the active cycle, with their shares, recipient status, and outstanding balance.
              </p>
            </div>
            <div className={`hidden sm:flex items-center justify-center w-10 h-10 rounded-xl ${theme.bg} ${theme.text} border ${theme.border}`}>
              <Users size={18} />
            </div>
          </div>
          {!activeCycle ? (
            <p className="text-xs text-gray-500">No active cycle available.</p>
          ) : isLoading ? (
            <p className="text-xs text-gray-500">Loading cycle members...</p>
          ) : cycleMembers.length === 0 ? (
            <p className="text-xs text-gray-500">No cycle members found.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-1.5 sm:gap-3">
                <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                  <Users size={16} className={`${theme.text} mb-1 sm:mb-2`} />
                  <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Members</p>
                  <p className={`text-base sm:text-2xl font-bold my-0.5 sm:my-1 ${theme.text}`}>{cycleMemberStats.memberCount}</p>
                  <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">In Cycle</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                  <Layers size={16} className="text-gray-400 mb-1 sm:mb-2" />
                  <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Shares</p>
                  <p className="text-base sm:text-2xl font-bold text-gray-900 my-0.5 sm:my-1">{cycleMemberStats.totalShares}</p>
                  <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Total Shares</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                  <Trophy size={16} className="text-amber-500 mb-1 sm:mb-2" />
                  <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Recipients</p>
                  <p className="text-base sm:text-2xl font-bold text-amber-600 my-0.5 sm:my-1">{cycleMemberStats.recipientCount}</p>
                  <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Drawn So Far</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                  <Wallet size={16} className="text-rose-500 mb-1 sm:mb-2" />
                  <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Due</p>
                  <p className={`text-base sm:text-2xl font-bold my-0.5 sm:my-1 ${cycleMemberStats.totalDue > 0 ? "text-rose-600" : "text-gray-900"}`}>
                    {formatMoney(cycleMemberStats.totalDue)}
                  </p>
                  <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Total Outstanding</p>
                </div>
              </div>

              <input
                type="text"
                value={cycleMembersSearch}
                onChange={(e) => setCycleMembersSearch(e.target.value)}
                placeholder="Search member by name..."
                className={`w-full md:max-w-xs px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
              />

              {filteredCycleMembers.length === 0 ? (
                <p className="text-xs text-gray-500">No members match your search.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-left border-collapse text-[10px] sm:text-xs">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                      <tr>
                        <th className="px-2 py-2">Member</th>
                        <th className="px-2 py-2 hidden sm:table-cell">Role</th>
                        <th className="px-2 py-2 text-center">Shares</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="px-2 py-2 text-right">Due</th>
                        <th className="px-2 py-2 hidden md:table-cell text-center">Won</th>
                        <th className="px-2 py-2 text-right">Total Won</th>
                        <th className="px-2 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600 bg-white">
                      {visibleCycleMembers.map((member) => {
                        const statusMeta = getCycleMemberStatusMeta(member)
                        const isMine = String(member?.id || "") === String(currentMemberId || "")
                        return (
                          <tr key={member.id} className={`hover:bg-gray-50 ${isMine ? `${theme.bg}/40` : ""}`}>
                            <td className="px-2 py-2">
                              <span className="font-medium text-gray-900">{member.name}</span>
                              {isMine && <span className={`ml-1 ${theme.text}`}>(You)</span>}
                              <div className="text-[9px] text-gray-400 sm:hidden capitalize">{member.role}</div>
                            </td>
                            <td className="px-2 py-2 hidden sm:table-cell capitalize text-gray-700">{member.role}</td>
                            <td className="px-2 py-2 text-center text-gray-900">{member.shareCount || 1}</td>
                            <td className="px-2 py-2">
                              <span className={`inline-flex items-center justify-center whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-semibold border leading-tight ${statusMeta.className}`}>
                                {statusMeta.label}
                              </span>
                            </td>
                            <td className={`px-2 py-2 text-right font-medium ${Number(member?.dueAmount || 0) > 0 ? "text-red-600" : "text-gray-400"}`}>
                              {formatMoney(member.dueAmount || 0)}
                            </td>
                            <td className="px-2 py-2 hidden md:table-cell text-center text-gray-700">
                              {member.wonShares || 0}
                              {member.wonRound && (
                                <span className="text-[9px] text-gray-400 ml-1">(R{member.wonRound})</span>
                              )}
                            </td>
                            <td className={`px-2 py-2 text-right font-medium ${theme.text}`}>
                              {Number(member?.totalWonAmount || 0) > 0 ? (
                                formatMoney(member.totalWonAmount)
                              ) : (
                                <span className="text-[10px] text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right">
                              {Number(member?.dueAmount || 0) > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => handleRequestCycleMemberPayment(member)}
                                  disabled={!canPayInvoices || String(isPayingId || "") === `member:${member.id}`}
                                  className={`inline-flex min-w-[30px] justify-center px-2 py-1 rounded text-[10px] font-semibold text-white disabled:opacity-50 whitespace-nowrap transition-all ${theme.button}`}
                                >
                                  {String(isPayingId || "") === `member:${member.id}` ? "..." : "Pay"}
                                </button>
                              ) : (
                                <span className="text-[10px] text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredCycleMembers.length > visibleCycleMembers.length && (
                <button
                  type="button"
                  onClick={() => setCycleMembersVisibleCount((prev) => prev + 5)}
                  className={`px-3 py-1.5 rounded text-[10px] font-semibold text-white transition-all ${theme.button}`}
                >
                  Show More
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-md p-2 md:p-4">
        <div className="mb-2 px-1 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Outstanding Payments</h4>
            {!isProjectSavingsGroup && !isChamaaGroup && !isWelfareGroup && (
              <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                <button
                  type="button"
                  onClick={() => setInvoiceScope("current")}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${invoiceScope === "current"
                    ? "bg-slate-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Current
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceScope("all")}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${invoiceScope === "all"
                    ? "bg-slate-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  All
                </button>
              </div>
            )}
          </div>
          {isChamaaGroup && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
              <span>Pending: <span className="font-semibold text-gray-700">{outstandingSummary.pendingCount}</span></span>
              <span>Overdue: <span className="font-semibold text-gray-700">{outstandingSummary.overdueCount}</span></span>
              <span>Total Due: <span className="font-semibold text-gray-700">{outstandingSummary.totalDueCount}</span></span>
              <span>Amount: <span className="font-semibold text-red-600">{formatMoney(outstandingSummary.totalAmount)}</span></span>
            </div>
          )}
          {isWelfareGroup && !isChamaaGroup && (
            <div className="flex items-center justify-between gap-2 whitespace-nowrap text-[9px] sm:text-[10px] text-gray-500">
              <span className="shrink-0">
                Total outstanding:{" "}
                <span className="font-semibold text-red-600">{formatMoney(outstandingSummary.totalAmount)}</span>
              </span>
              <span className="shrink-0">
                Invoices: <span className="font-semibold text-gray-700">{orderedGroupInvoices.length}</span>
              </span>
              <span className="shrink-0 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-right text-red-700">
                My Outstanding: <span className="font-bold text-red-700">{formatMoney(myWelfareOutstandingAmount)}</span>
              </span>
            </div>
          )}
        </div>
        {isLoading ? (
          <p className="text-xs text-gray-500 px-1">Loading outstanding invoices...</p>
        ) : orderedGroupInvoices.length === 0 ? (
          <p className="text-xs text-gray-500 px-1">{noOutstandingMessage}</p>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full table-fixed text-left border-collapse text-[8px] sm:text-[10px] md:text-[11px] leading-tight">
                <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                  <tr className="text-[8px] sm:text-[10px] md:text-[11px]">
                    <th className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 w-[14%]">Due Date</th>
                    <th className={`px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 ${isWelfareGroup && !isChamaaGroup ? "w-[22%]" : "w-[28%]"}`}>
                      Member
                    </th>
                    {isChamaaGroup && (
                      <th className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 w-[13%]">Cyc/Rnd</th>
                    )}
                    {isWelfareGroup && !isChamaaGroup && (
                      <th className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 w-[10%]">Ref #</th>
                    )}
                    <th className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 w-[16%]">Status</th>
                    <th className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 w-[14%] text-right">
                      {isProjectSavingsGroup ? "Remaining" : "Amount"}
                    </th>
                    <th className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 w-[12%] text-right hidden sm:table-cell">Paid</th>
                    {!isChamaaGroup && (
                      <th className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 w-[12%] text-right">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-[8px] sm:text-[10px] md:text-[11px] text-gray-600">
                  {visibleOutstandingInvoices.map((invoice) => {
                    const invoiceDisplayStatus = resolveInvoiceDisplayStatus(invoice)
                    const isMine = String(invoice?.memberId || "") === String(memberInfo?.id || "")
                    const isWelfareOwnBalanceAction = isWelfareGroup && isMine
                    const invoicePayingKey = isWelfareOwnBalanceAction
                      ? `member:${currentMemberId || ""}`
                      : String(invoice?.id || "")
                    const canPay =
                      canPayInvoices &&
                      Number(invoice?.balanceAmount || 0) > 0 &&
                      invoice?.status !== "paid" &&
                      invoice?.status !== "cancelled"
                    const invoiceDueDate = invoice?.dueDate || null
                    const fallbackWelfareMemberNumber = Number(invoice?.welfareMemberNumber || 0)
                    const effectiveWelfareMemberIdentifier = formatWelfareMemberIdentifier(
                      invoice?.welfareMemberIdentifier,
                      fallbackWelfareMemberNumber
                    )
                    const rawReferenceCode = String(invoice?.referenceCode || "").trim()
                    const effectiveReferenceCode =
                      rawReferenceCode ||
                      (isWelfareGroup && Number(invoice?.roundNumber || 0) === 1 ? "REG" : "")
                    return (
                      <tr key={invoice.id} className={`hover:bg-gray-50 ${isMine ? `${theme.bg}/40` : ""}`}>
                        <td className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 whitespace-normal break-words text-gray-700">
                          {invoiceDueDate ? (
                            <span>{formatDateShort(invoiceDueDate)}</span>
                          ) : "-"}
                        </td>
                        <td className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 font-medium text-gray-900">
                          <div className="whitespace-normal break-words leading-tight">
                            {invoice.memberName || "-"}
                            {isWelfareGroup && effectiveWelfareMemberIdentifier ? (
                              <span className="text-gray-500"> ({effectiveWelfareMemberIdentifier})</span>
                            ) : null}{" "}
                            {isMine ? <span className={`${theme.text}`}>(You)</span> : null}
                          </div>
                        </td>
                        {isChamaaGroup && (
                          <td className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 whitespace-normal break-words text-gray-700">
                            {`C${invoice?.cycleSequence || "-"}/R${invoice?.roundNumber || "-"}`}
                          </td>
                        )}
                        {isWelfareGroup && !isChamaaGroup && (
                          <td className="px-1 py-1 sm:px-1.5 sm:py-1.5 md:px-2 md:py-2 whitespace-nowrap">
                            {effectiveReferenceCode ? (
                              <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[8px] sm:text-[9px] font-semibold text-slate-700">
                                {effectiveReferenceCode}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 whitespace-normal">
                          <StatusBadge status={invoiceDisplayStatus} />
                        </td>
                        <td className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 text-right whitespace-nowrap">
                          {formatMoney(isProjectSavingsGroup ? invoice.balanceAmount || 0 : invoice.amount || 0)}
                        </td>
                        <td className="px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 text-right hidden sm:table-cell text-emerald-600 whitespace-nowrap">
                          {formatMoney(invoice.paidAmount || 0)}
                        </td>
                        {!isChamaaGroup && (
                          <td className="px-1 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2 text-right">
                            <button
                              type="button"
                              disabled={!canPay || String(isPayingId || "") === String(invoicePayingKey)}
                              onClick={() => {
                                if (isWelfareOwnBalanceAction) {
                                  handleRequestMyWelfareBalancePayment()
                                } else {
                                  handleRequestPayInvoice(invoice)
                                }
                              }}
                              className={`inline-flex min-w-[30px] justify-center px-1 py-0.5 sm:px-1.5 sm:py-1 rounded text-[8px] sm:text-[9px] font-semibold text-white disabled:opacity-50 whitespace-nowrap transition-all ${theme.button}`}
                            >
                              {String(isPayingId || "") === String(invoicePayingKey) ? "..." : "Pay"}
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {canShowMoreOutstandingInvoices && (
              <div className="mt-2 px-1">
                <button
                  type="button"
                  onClick={() => setOutstandingVisibleCount((prev) => prev + 5)}
                  className={`px-3 py-1.5 rounded text-[10px] font-semibold text-white transition-all ${theme.button}`}
                >
                  Show More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isMyOutstandingOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-3 sm:p-5">
          <div className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-gray-900">{myTransactionsLabel}</h4>
                {isWelfareGroup ? (
                  <>
                    <p className="mt-1 text-xs text-gray-500">
                      Current total (OUT - IN):{" "}
                      <span className={`font-semibold ${myCurrentTransactionTotal < 0 ? "text-red-600" : "text-gray-800"}`}>
                        {formatMoney(myCurrentTransactionTotal)}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Owes:{" "}
                      <span className={`font-semibold ${outstandingAmount > 0 ? "text-red-600" : "text-gray-800"}`}>
                        {formatMoney(outstandingAmount)}
                      </span>
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Current outstanding:{" "}
                    <span className={`font-semibold ${outstandingAmount > 0 ? "text-red-600" : "text-gray-800"}`}>
                      {formatMoney(outstandingAmount)}
                    </span>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsMyOutstandingOpen(false)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                Close
              </button>
            </div>

            <div className="px-4 py-4 sm:px-5 border-b border-gray-200 bg-gray-50/80">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-gray-600">
                {isWelfareGroup && (
                  <>
                    <span className="inline-flex items-center whitespace-nowrap">
                      Paid: <span className="ml-1 font-semibold text-emerald-700">{formatMoney(welfarePaidTotal)}</span>
                    </span>
                    <span className="inline-flex items-center whitespace-nowrap">
                      Benefit: <span className="ml-1 font-semibold text-blue-700">{formatMoney(welfareBenefitedTotal)}</span>
                    </span>
                    <span className="inline-flex items-center whitespace-nowrap">
                      Owes: <span className="ml-1 font-semibold text-red-600">{formatMoney(welfareBenefitOutstandingAmount)}</span>
                    </span>
                  </>
                )}
                {!isWelfareGroup && (
                  <span className="inline-flex items-center whitespace-nowrap">
                    Invoice Outstanding: <span className="ml-1 font-semibold text-red-600">{formatMoney(invoiceOutstandingAmount)}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-[62vh] overflow-y-auto overflow-x-hidden px-4 pt-4 pb-20 sm:px-5">
              {isLoading ? (
                <p className="text-xs text-gray-500">Loading outstanding details...</p>
              ) : myOutstandingLedgerRows.length === 0 ? (
                <p className="text-xs text-gray-500">No transactions found for your account.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="w-full table-fixed text-left border-collapse text-[10px] sm:text-xs mb-8">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border} sticky top-0`}>
                      <tr>
                        <th className={`${isWelfareGroup ? "w-[22%] md:w-[20%]" : "w-[25%] md:w-[22%]"} px-2 py-2 sm:px-3`}>Date</th>
                        <th className={`${isWelfareGroup ? "w-[38%] md:w-[36%]" : "w-[40%] md:w-[43%]"} px-2 py-2 sm:px-3`}>Description</th>
                        {isWelfareGroup && (
                          <th className="w-[12%] md:w-[10%] px-2 py-2 sm:px-3">Ref #</th>
                        )}
                        <th className="w-[14%] md:w-[14%] px-2 py-2 sm:px-3 text-right">Amount</th>
                        <th className="w-[14%] md:w-[14%] px-2 py-2 sm:px-3 text-right">My Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600">
                      {myOutstandingLedgerRows.map((row) => {
                        const isPositive = Number(row?.amount || 0) >= 0
                        const longDateText = row?.date ? formatDateLong(row.date) : ""
                        const shortDateText = row?.date ? formatDateShort(row.date) : ""
                        const rawReferenceCode = String(row?.referenceCode || "").trim()
                        const effectiveReferenceCode = rawReferenceCode
                        return (
                          <tr key={`my-outstanding-${row.id}`} className="hover:bg-gray-50 align-top">
                            <td className="px-2 py-2 sm:px-3 whitespace-normal leading-tight">
                              {row?.date ? (
                                <span className="sm:inline">{longDateText || shortDateText || "-"}</span>
                              ) : "-"}
                            </td>
                            <td className="px-2 py-2 sm:px-3">
                              <div className="font-semibold text-gray-900 break-words">{row?.description || "-"}</div>
                            </td>
                            {isWelfareGroup && (
                              <td className="px-2 py-2 sm:px-3 whitespace-nowrap">
                                {effectiveReferenceCode ? (
                                  <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">
                                    {effectiveReferenceCode}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            )}
                            <td className={`px-2 py-2 sm:px-3 text-right font-semibold whitespace-nowrap ${isPositive ? "text-emerald-600" : "text-rose-600"}`}>
                              {formatMoney(row?.amount || 0)}
                            </td>
                            <td className={`px-2 py-2 sm:px-3 text-right font-semibold whitespace-nowrap ${Number(row?.total || 0) < 0 ? "text-red-600" : "text-gray-900"}`}>
                              {formatMoney(row?.total || 0)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isWalletTransactionsOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-3 sm:p-5">
          <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-gray-900">Group Wallet Transactions</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Current wallet balance:{" "}
                  <span className="font-semibold text-gray-800">
                    {formatMoney(groupWalletBalanceValue)}
                  </span>
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-stretch gap-2">
                <button
                  type="button"
                  onClick={closeWalletTransactionsModal}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
                >
                  Close
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddBillModalOpen(true)
                      setBillAmountInput("")
                      setBillDescriptionInput("")
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white border ${theme.border} ${theme.button}`}
                  >
                    Add Bill
                  </button>
                )}
              </div>
            </div>

            <div className="px-4 py-4 sm:px-5 border-b border-gray-200 bg-gray-50/80">
                {isWelfareGroup && (
                  <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setWalletTransactionsFilterOpen((prev) => !prev)}
                      className="w-full flex items-center justify-between text-slate-800 font-medium text-sm"
                    >
                      <span>Filter</span>
                      {walletTransactionsFilterOpen ? (
                        <ChevronUp size={16} className="text-slate-600" />
                      ) : (
                        <ChevronDown size={16} className="text-slate-600" />
                      )}
                    </button>
                    {walletTransactionsFilterOpen && (
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={walletTransactionsStartDate}
                            onChange={(e) => setWalletTransactionsStartDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={walletTransactionsEndDate}
                            onChange={(e) => setWalletTransactionsEndDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={applyWalletTransactionsDateFilter}
                            className="flex-1 sm:flex-none bg-sky-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-sky-700 transition-colors font-medium text-sm"
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={clearWalletTransactionsDateFilter}
                            className="flex-1 sm:flex-none bg-slate-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors font-medium text-sm"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-3 ${isChamaaGroup ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">
                    Search By Name
                  </label>
                  <input
                    type="text"
                    value={walletTransactionsSearch}
                    onChange={(e) => setWalletTransactionsSearch(e.target.value)}
                    placeholder="Type member name..."
                    className={`w-full px-3 py-2.5 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                  />
                </div>
                {isChamaaGroup && (
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Cycle</label>
                    <select
                      value={walletTransactionsCycleFilter}
                      onChange={(e) => setWalletTransactionsCycleFilter(e.target.value)}
                      className={`w-full px-3 py-2.5 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                    >
                      <option value="all">All cycles</option>
                      {walletCycleOptions.map((cycleNo) => (
                        <option key={cycleNo} value={String(cycleNo)}>
                          {`C${cycleNo}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-end justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-600">
                      Showing <span className="font-semibold text-gray-900">{filteredWalletLedgerRows.length}</span> of{" "}
                      <span className="font-semibold text-gray-900">{walletLedgerRowsDateFiltered.length}</span> rows
                    </p>
                    {isChamaaGroup && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
                        <div className="flex items-center gap-1">
                          <span className="flex items-center justify-center w-3.5 h-3.5 rounded bg-blue-100 text-[8px] font-bold text-blue-700 border border-blue-200 shadow-sm">C</span>
                          <span className="text-[10px] text-gray-500 font-medium">Cycle</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="flex items-center justify-center w-3.5 h-3.5 rounded bg-amber-100 text-[8px] font-bold text-amber-700 border border-amber-200 shadow-sm">R</span>
                          <span className="text-[10px] text-gray-500 font-medium">Round</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="flex items-center justify-center w-3.5 h-3.5 rounded bg-emerald-100 text-[8px] font-bold text-emerald-700 border border-emerald-200 shadow-sm">S</span>
                          <span className="text-[10px] text-gray-500 font-medium">Share</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {isWelfareGroup && (
                    <div className="flex flex-col items-end justify-end gap-1 sm:flex-row sm:items-center sm:gap-3 text-[10px] text-gray-600">
                      <span className="inline-flex items-center whitespace-nowrap">
                        Paid:{" "}
                        <span className="ml-1 font-semibold text-emerald-700">
                          {formatMoney(welfareDateFilteredTotals.paidTotal)}
                        </span>
                      </span>
                      <span className="inline-flex items-center whitespace-nowrap">
                        Benefited:{" "}
                        <span className="ml-1 font-semibold text-blue-700">
                          {formatMoney(welfareDateFilteredTotals.benefitedTotal)}
                        </span>
                      </span>
                      <span className="inline-flex items-center whitespace-nowrap">
                        Group expenses:{" "}
                        <span className="ml-1 font-semibold text-amber-700">
                          {formatMoney(welfareDateFilteredTotals.groupExpensesTotal)}
                        </span>
                      </span>
                      <span className="inline-flex items-center whitespace-nowrap">
                        Total Welfare Transactions:{" "}
                        <span className="ml-1 font-semibold text-red-600">
                          {formatMoney(welfareDateFilteredTotals.transactionsTotal)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="max-h-[62vh] overflow-y-auto overflow-x-hidden px-4 pt-4 pb-20 sm:px-5">
              {isLoading ? (
                <p className="text-xs text-gray-500">Loading wallet transactions...</p>
              ) : filteredWalletLedgerRows.length === 0 ? (
                <p className="text-xs text-gray-500">No transactions found for selected filters.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="w-full table-fixed text-left border-collapse text-[10px] sm:text-xs mb-8">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border} sticky top-0`}>
                      <tr>
                        <th className={`${isWelfareGroup ? "w-[19%] md:w-[17%]" : "w-[22%] md:w-[20%]"} px-2 py-2 sm:px-3`}>Date</th>
                        <th className={`${isWelfareGroup ? "w-[35%] md:w-[31%]" : isChamaaGroup ? "w-[44%] md:w-[36%]" : "w-[44%] md:w-[50%]"} px-2 py-2 sm:px-3`}>Description</th>
                        {isWelfareGroup && (
                          <th className="w-[12%] md:w-[10%] px-2 py-2 sm:px-3">Ref #</th>
                        )}
                        {isChamaaGroup && (
                          <th className="hidden md:table-cell w-[14%] px-2 py-2 sm:px-3">Cycle/Round</th>
                        )}
                        <th className={`${isWelfareGroup ? "w-[17%] md:w-[14%]" : isChamaaGroup ? "w-[17%] md:w-[15%]" : "w-[17%] md:w-[15%]"} px-2 py-2 sm:px-3 text-right`}>Amount</th>
                        <th className={`${isWelfareGroup ? "w-[17%] md:w-[14%]" : "w-[17%] md:w-[15%]"} px-2 py-2 sm:px-3 text-right`}>Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600">
                      {filteredWalletLedgerRows.map((row) => {
                        const isPositive = Number(row?.amount || 0) >= 0
                        const displayDescription = isWelfareGroup
                          ? invertInOutPrefix(row?.description || "")
                          : String(row?.description || "")
                        const longDateText = row?.date ? formatDateLong(row.date) : ""
                        const shortDateText = row?.date ? formatDateShort(row.date) : ""
                        const fallbackWelfareMemberNumber = Number(row?.welfareMemberNumber || 0)
                        const effectiveWelfareMemberIdentifier = formatWelfareMemberIdentifier(
                          row?.welfareMemberIdentifier,
                          fallbackWelfareMemberNumber
                        )
                        const memberDisplayName = row?.memberName || "-"
                        const rawReferenceCode = String(row?.referenceCode || "").trim()
                        const effectiveReferenceCode =
                          rawReferenceCode ||
                          (isWelfareGroup && Number(row?.roundNumber || 0) === 1 ? "REG" : "")
                        return (
                          <tr key={row.id} className="hover:bg-gray-50 align-top">
                            <td className="px-2 py-2 sm:px-3 whitespace-normal leading-tight">
                              {row?.date ? (
                                <>
                                  <span className="sm:inline">{longDateText || shortDateText || "-"}</span>
                                </>
                              ) : "-"}
                            </td>
                            <td className="px-2 py-2 sm:px-3">
                              <div className="font-semibold text-gray-900 break-words">{displayDescription || "-"}</div>
                              <div className="text-[10px] sm:text-[11px] text-gray-500 break-words">
                                {memberDisplayName}
                                {isWelfareGroup && effectiveWelfareMemberIdentifier
                                  ? ` (${effectiveWelfareMemberIdentifier})`
                                  : ""}
                              </div>
                              {isChamaaGroup && (
                                <div className="mt-0.5 text-[10px] text-gray-500 md:hidden">
                                  {`C${row?.cycleSequence || "-"}/R${row?.roundNumber || "-"}`}
                                </div>
                              )}
                            </td>
                            {isWelfareGroup && (
                              <td className="px-2 py-2 sm:px-3 whitespace-nowrap">
                                {effectiveReferenceCode ? (
                                  <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">
                                    {effectiveReferenceCode}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            )}
                            {isChamaaGroup && (
                              <td className="hidden md:table-cell px-2 py-2 sm:px-3 whitespace-nowrap font-medium text-gray-700">
                                {`C${row?.cycleSequence || "-"}/R${row?.roundNumber || "-"}`}
                              </td>
                            )}
                            <td className={`px-2 py-2 sm:px-3 text-right font-semibold whitespace-nowrap ${isPositive ? "text-emerald-600" : "text-rose-600"}`}>
                              {formatMoney(row?.amount || 0)}
                            </td>
                            <td className="px-2 py-2 sm:px-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                              {formatMoney(row?.total || 0)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isAddBillModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white border border-gray-200 shadow-2xl p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Add Bill</h4>
            <p className="text-xs text-gray-500">
              Current group wallet balance:{" "}
              <span className="font-semibold text-gray-800">{formatMoney(groupWalletBalanceValue)}</span>
            </p>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                Description
              </label>
              <input
                type="text"
                value={billDescriptionInput}
                onChange={(e) => setBillDescriptionInput(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${billDescriptionValidationMessage
                  ? "border-red-300 focus:ring-red-300"
                  : `${theme.border} ${theme.ring}`
                  }`}
                placeholder="Bill description"
              />
              {!!billDescriptionValidationMessage && (
                <p className="text-[10px] text-red-600 mt-1">{billDescriptionValidationMessage}</p>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                Bill Amount
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={billAmountInput}
                onChange={(e) => setBillAmountInput(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${billAmountValidationMessage
                  ? "border-red-300 focus:ring-red-300"
                  : `${theme.border} ${theme.ring}`
                  }`}
                placeholder="0.00"
              />
              {!!billAmountValidationMessage && (
                <p className="text-[10px] text-red-600 mt-1">{billAmountValidationMessage}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeAddBillModal}
                className="px-3 py-1.5 rounded text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddBill}
                disabled={!!addBillValidationMessage || isAddingBill}
                className={`px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-60 ${theme.button}`}
              >
                {isAddingBill ? "Adding..." : "Add Bill"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingCycleMemberPayment && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full max-w-md rounded-2xl bg-white shadow-xl p-4 border ${pendingCycleMemberPayment?.isMine
              ? "border-gray-200"
              : "border-orange-300 ring-1 ring-red-200"
              }`}
          >
            {pendingCycleMemberPayment?.isMine ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-2xl font-semibold tracking-tight text-gray-900">Pay Balance</h4>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-400">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-lg font-semibold text-gray-900">
                      {pendingCycleMemberPayment?.memberName || "Member"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 border-y border-gray-100 py-3">
                  <div>
                    <p className="text-sm text-gray-500">Outstanding</p>
                    <p className="mt-1 text-2xl font-semibold leading-none text-red-500">
                      {formatMoney(pendingCycleMemberPayment?.totalDue || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Wallet</p>
                    <p className="mt-1 text-2xl font-semibold leading-none text-gray-900">
                      {formatMoney(walletBalance || 0)}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCycleMemberPaymentMode("full")
                        setCycleMemberPaymentAmount(
                          String(Number(pendingCycleMemberPayment?.totalDue || 0))
                        )
                      }}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${cycleMemberPaymentMode === "full"
                        ? `${theme.border} ${theme.bg} ${theme.text}`
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      Pay Full Balance
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCycleMemberPaymentMode("increment")
                        const defaultIncrement = Number(cycleMemberPaymentOptions?.[0] || 0)
                        setCycleMemberPaymentAmount(defaultIncrement > 0 ? String(defaultIncrement) : "")
                      }}
                      disabled={cycleMemberPaymentOptions.length === 0}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 ${cycleMemberPaymentMode === "increment"
                        ? `${theme.border} ${theme.bg} ${theme.text}`
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      Pay Oldest Invoices
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Amount</label>
                    <div className="grid grid-cols-[56px_minmax(0,1fr)_56px] gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const currentAmount = Number(cycleMemberPaymentAmount || 0)
                          const previousOption = [...cycleMemberPaymentOptions]
                            .reverse()
                            .find((option) => Number(option || 0) < currentAmount - 0.0001)
                          if (previousOption) {
                            setCycleMemberPaymentMode("increment")
                            setCycleMemberPaymentAmount(String(previousOption))
                          }
                        }}
                        disabled={
                          Number(cycleMemberPaymentAmount || 0) <=
                          Number(cycleMemberPaymentOptions?.[0] || 0)
                        }
                        className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-2xl font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        -
                      </button>
                      <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-3 text-2xl font-semibold text-gray-900">
                        {formatMoney(cycleMemberPaymentAmount || 0)}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const currentAmount = Number(cycleMemberPaymentAmount || 0)
                          const nextOption = cycleMemberPaymentOptions.find(
                            (option) => Number(option || 0) > currentAmount + 0.0001
                          )
                          if (nextOption) {
                            const totalDue = Number(pendingCycleMemberPayment?.totalDue || 0)
                            setCycleMemberPaymentMode(
                              Math.abs(nextOption - totalDue) < 0.0001 ? "full" : "increment"
                            )
                            setCycleMemberPaymentAmount(String(nextOption))
                          }
                        }}
                        disabled={
                          Number(cycleMemberPaymentAmount || 0) >=
                          Number(pendingCycleMemberPayment?.totalDue || 0)
                        }
                        className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-2xl font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        +
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Will clear:{" "}
                      <span className="font-semibold text-gray-700">
                        {selectedCycleMemberPaidInvoiceCount}
                      </span>{" "}
                      oldest invoice{selectedCycleMemberPaidInvoiceCount === 1 ? "" : "s"}
                    </p>
                    {cycleMemberPaymentMode === "increment" && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        Each amount must fully cover the oldest unpaid invoice(s) in order.
                      </p>
                    )}
                  </div>

                  {!!cycleMemberPaymentValidationMessage && (
                    <p className="text-xs text-red-600">{cycleMemberPaymentValidationMessage}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <button
                    type="button"
                    onClick={closePendingCycleMemberPaymentModal}
                    className="rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectedCycleMemberPayment}
                    disabled={
                      !!cycleMemberPaymentValidationMessage ||
                      String(isPayingId || "") ===
                      `member:${pendingCycleMemberPayment?.memberId || ""}`
                    }
                    className={`rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60 ${theme.button}`}
                  >
                    {String(isPayingId || "") === `member:${pendingCycleMemberPayment?.memberId || ""}`
                      ? "Processing..."
                      : `Pay ${formatMoney(cycleMemberPaymentAmount || 0)}`}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Pay For Another Member</h4>
                <p className="text-xs text-gray-600 mb-1">
                  You are about to pay for{" "}
                  <span className="font-semibold text-gray-900">
                    {pendingCycleMemberPayment?.memberName || "this member"}
                  </span>
                  .
                </p>
                <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-red-800">
                    Serious Confirmation Required
                  </p>
                  <p className="mt-1 text-xs text-red-700">
                    You are paying from <span className="font-semibold">your Groups Wallet</span> for{" "}
                    <span className="font-semibold">
                      {pendingCycleMemberPayment?.memberName || "this member"}
                    </span>
                    . This cannot be mistaken for your own payment.
                  </p>
                  <p className="mt-1 text-xs text-red-700">
                    Other group members will not see that you covered this balance, but your Groups Wallet
                    will still be charged.
                  </p>
                  <label className="mt-3 flex items-start gap-2 text-xs text-red-800">
                    <input
                      type="checkbox"
                      checked={proxyCyclePaymentConfirmed}
                      onChange={(e) => setProxyCyclePaymentConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-400"
                    />
                    <span className="font-semibold">
                      I understand I am paying another member&apos;s balance with my own Groups Wallet.
                    </span>
                  </label>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Outstanding</p>
                      <p className="text-xs font-semibold text-red-600">
                        {formatMoney(pendingCycleMemberPayment?.totalDue || 0)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Oldest Invoice</p>
                      <p className={`text-xs font-semibold ${theme.text}`}>
                        {formatMoney(pendingCycleMemberPayment?.shareUnitAmount || 0)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCycleMemberPaymentMode("full")
                        setCycleMemberPaymentAmount(
                          String(Number(pendingCycleMemberPayment?.totalDue || 0))
                        )
                      }}
                      className={`rounded-lg border px-3 py-2 text-left ${cycleMemberPaymentMode === "full"
                        ? `${theme.border} ${theme.bg} ${theme.text}`
                        : "border-gray-200 bg-white text-gray-700"
                        }`}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-wide">Pay Full Balance</p>
                      <p className="text-xs font-semibold mt-1">
                        {formatMoney(pendingCycleMemberPayment?.totalDue || 0)}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCycleMemberPaymentMode("increment")
                        const defaultIncrement = Number(cycleMemberPaymentOptions?.[0] || 0)
                        setCycleMemberPaymentAmount(defaultIncrement > 0 ? String(defaultIncrement) : "")
                      }}
                      disabled={cycleMemberPaymentOptions.length === 0}
                      className={`rounded-lg border px-3 py-2 text-left disabled:opacity-50 ${cycleMemberPaymentMode === "increment"
                        ? `${theme.border} ${theme.bg} ${theme.text}`
                        : "border-gray-200 bg-white text-gray-700"
                        }`}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-wide">
                        Pay Oldest Invoices
                      </p>
                      <p className="text-xs font-semibold mt-1">
                        {cycleMemberPaymentOptions.length > 0
                          ? `Start ${formatMoney(cycleMemberPaymentOptions[0])}`
                          : "Not available"}
                      </p>
                    </button>
                  </div>

                  {cycleMemberPaymentMode === "increment" && (
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                        Payment Amount
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const currentAmount = Number(cycleMemberPaymentAmount || 0)
                            const previousOption = [...cycleMemberPaymentOptions]
                              .reverse()
                              .find((option) => Number(option || 0) < currentAmount - 0.0001)
                            if (previousOption) {
                              setCycleMemberPaymentAmount(String(previousOption))
                            }
                          }}
                          disabled={
                            Number(cycleMemberPaymentAmount || 0) <=
                            Number(cycleMemberPaymentOptions?.[0] || 0)
                          }
                          className="px-3 py-2 rounded-lg text-sm font-bold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          -
                        </button>
                        <div className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            Current Increment
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatMoney(cycleMemberPaymentAmount || 0)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const currentAmount = Number(cycleMemberPaymentAmount || 0)
                            const nextOption = cycleMemberPaymentOptions.find(
                              (option) => Number(option || 0) > currentAmount + 0.0001
                            )
                            if (nextOption) {
                              setCycleMemberPaymentAmount(String(nextOption))
                            }
                          }}
                          disabled={
                            Number(cycleMemberPaymentAmount || 0) >=
                            Number(pendingCycleMemberPayment?.totalDue || 0)
                          }
                          className="px-3 py-2 rounded-lg text-sm font-bold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        Each amount must fully cover the oldest unpaid invoice(s) in order.
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{myWalletBalanceLabel}</p>
                    <p className={`text-xs font-semibold ${theme.text}`}>
                      {formatMoney(walletBalance || 0)}
                    </p>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Selected Amount</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatMoney(cycleMemberPaymentAmount || 0)}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      This will pay the oldest{" "}
                      <span className="font-semibold text-gray-700">
                        {selectedCycleMemberPaidInvoiceCount}
                      </span>{" "}
                      invoice{selectedCycleMemberPaidInvoiceCount === 1 ? "" : "s"} first.
                    </p>
                  </div>

                  {!!cycleMemberPaymentValidationMessage && (
                    <p className="text-[10px] text-red-600">{cycleMemberPaymentValidationMessage}</p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={closePendingCycleMemberPaymentModal}
                    className="px-3 py-1.5 rounded text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectedCycleMemberPayment}
                    disabled={
                      !proxyCyclePaymentConfirmed ||
                      !!cycleMemberPaymentValidationMessage ||
                      String(isPayingId || "") ===
                      `member:${pendingCycleMemberPayment?.memberId || ""}`
                    }
                    className={`px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-60 transition-all ${theme.button}`}
                  >
                    {String(isPayingId || "") === `member:${pendingCycleMemberPayment?.memberId || ""}`
                      ? "Processing..."
                      : "Confirm"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {pendingProxyPaymentInvoice && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full max-w-md rounded-2xl bg-white shadow-xl p-4 border ${String(pendingProxyPaymentInvoice?.memberId || "") === String(memberInfo?.id || "")
              ? "border-gray-200"
              : "border-orange-300 ring-1 ring-red-200"
              }`}
          >
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              {String(pendingProxyPaymentInvoice?.memberId || "") === String(memberInfo?.id || "")
                ? "Pay Invoice"
                : "Pay For Another Member"}
            </h4>
            <p className="text-xs text-gray-600 mb-1">
              You are about to pay invoice for{" "}
              <span className="font-semibold text-gray-900">{pendingProxyPaymentInvoice?.memberName || "this member"}</span>.
            </p>
            {String(pendingProxyPaymentInvoice?.memberId || "") === String(memberInfo?.id || "") ? (
              <p className="text-xs text-gray-500 mb-4">
                {isChamaaGroup
                  ? "Chamaa invoices must be paid in full, starting with the oldest unpaid round."
                  : "Choose the exact amount you want to pay, or use full payment."}
              </p>
            ) : (
              <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-red-800">
                  Serious Confirmation Required
                </p>
                <p className="mt-1 text-xs text-red-700">
                  You are about to use <span className="font-semibold">your Groups Wallet</span> to pay{" "}
                  <span className="font-semibold">
                    {pendingProxyPaymentInvoice?.memberName || "this member"}
                  </span>
                  &apos;s invoice.
                </p>
                <p className="mt-1 text-xs text-red-700">
                  {isChamaaGroup
                    ? "This is still private, and Chamaa invoice order rules still apply."
                    : "This is private, but the money still leaves your Groups Wallet immediately."}
                </p>
                <label className="mt-3 flex items-start gap-2 text-xs text-red-800">
                  <input
                    type="checkbox"
                    checked={proxyInvoicePaymentConfirmed}
                    onChange={(e) => setProxyInvoicePaymentConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-400"
                  />
                  <span className="font-semibold">
                    I understand I am paying another member&apos;s invoice with my own Groups Wallet.
                  </span>
                </label>
              </div>
            )}
            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Outstanding</p>
                  <p className="text-xs font-semibold text-red-600">
                    {formatMoney(pendingProxyPaymentInvoice?.balanceAmount || 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">{myWalletBalanceLabel}</p>
                  <p className={`text-xs font-semibold ${theme.text}`}>
                    {formatMoney(walletBalance || 0)}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                  Amount To Pay
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={Number(pendingProxyPaymentInvoice?.balanceAmount || 0)}
                  value={paymentAmountInput}
                  onChange={(e) => setPaymentAmountInput(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${pendingPaymentValidationMessage
                    ? "border-red-300 focus:ring-red-300"
                    : `border-gray-300 ${theme.ring}`
                    }`}
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {isChamaaGroup
                    ? `Pay the full invoice amount of ${formatMoney(pendingProxyPaymentInvoice?.balanceAmount || 0)}.`
                    : `Enter any amount up to ${formatMoney(pendingProxyPaymentInvoice?.balanceAmount || 0)}.`}
                </p>
                {!!pendingPaymentValidationMessage && (
                  <p className="text-[10px] text-red-600 mt-1">{pendingPaymentValidationMessage}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closePendingPaymentModal}
                className="px-3 py-1.5 rounded text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSelectedPayment}
                disabled={
                  (String(pendingProxyPaymentInvoice?.memberId || "") !== String(memberInfo?.id || "") &&
                    !proxyInvoicePaymentConfirmed) ||
                  !!pendingPaymentValidationMessage ||
                  String(isPayingId || "") === String(pendingProxyPaymentInvoice?.id || "")
                }
                className={`px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-60 transition-all ${theme.button}`}
              >
                {String(isPayingId || "") === String(pendingProxyPaymentInvoice?.id || "") ? "Processing..." : "Pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!!error && (
        <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg">{error}</div>
      )}
      {!!success && (
        <div className="border border-green-300 bg-green-50 text-green-700 text-xs p-2 rounded-lg">{success}</div>
      )}
    </div>
      </fieldset>
    </div>
  )
}

function StatusBadge({ status }) {
  const styles = {
    paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    overdue: "bg-red-100 text-red-700 border-red-200",
    cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  }
  const key = String(status || "").toLowerCase()
  const cls = styles[key] || "bg-gray-100 text-gray-600 border-gray-200"
  const label = String(status || "-").toUpperCase()

  return (
    <span className={`inline-flex max-w-full items-center justify-center px-1 py-0.5 sm:px-1.5 rounded text-[8px] sm:text-[9px] font-semibold uppercase border text-center leading-tight whitespace-normal break-words ${cls}`}>
      {label}
    </span>
  )
}

export default GroupMemberFinancePanel
